import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"
import cytoscape from "cytoscape"
import cytoscapeDagre from "cytoscape-dagre"
import {
  createIcons,
  Pencil, X, Unlink, Link2, ExternalLink, TriangleAlert,
  CirclePlus, CircleMinus, ShieldAlert, Cable, Network, LayoutGrid,
  Eye, EyeOff, Maximize2, Minimize2, Trash2, Scissors, Link
} from "lucide"

// Register the dagre layout extension
cytoscape.use(cytoscapeDagre)

// Graph size thresholds for adaptive layout and label visibility
const LARGE_GRAPH_THRESHOLD = 500
const MEDIUM_GRAPH_THRESHOLD = 100

// i18n namespace (javascript.controllers.assembly_editor_controller is a garbage leaf string in
// javascript_auto.yml, so this controller's keys live under controllers_v2).
const ASSEMBLY_I18N = "javascript.controllers_v2.assembly_editor_controller"

// Lucide icon set used in dynamically generated HTML
const ASSEMBLY_ICONS = {
  Pencil, X, Unlink, Link2, ExternalLink, TriangleAlert,
  CirclePlus, CircleMinus, ShieldAlert, Cable, Network, LayoutGrid,
  Eye, EyeOff, Maximize2, Minimize2, Trash2, Scissors, Link
}

// Metadata field definitions per connection type.
// Labels/placeholders are translated at render time:
//   javascript.controllers_v2.assembly_editor_controller.metadata.<type>.<key>[_placeholder]
const METADATA_FIELDS = {
  pipe: [
    { key: 'pressure',    unit: 'psi', type: 'number', step: '0.1'   },
    { key: 'flow_rate',   unit: 'gpm', type: 'number', step: '0.1'   },
    { key: 'diameter',    unit: 'in',  type: 'number', step: '0.125' },
    { key: 'temperature', unit: '°C',  type: 'number', step: '1'     },
    { key: 'material',    unit: '',    type: 'text',   placeholder: true }
  ],
  electrical: [
    { key: 'voltage',   unit: 'V',  type: 'number', step: '1'   },
    { key: 'current',   unit: 'A',  type: 'number', step: '0.1' },
    { key: 'power',     unit: 'kW', type: 'number', step: '0.1' },
    { key: 'frequency', unit: 'Hz', type: 'number', step: '1', placeholder: true }
  ],
  hydraulic: [
    { key: 'pressure',   unit: 'psi', type: 'number', step: '0.1' },
    { key: 'flow_rate',  unit: 'gpm', type: 'number', step: '0.1' },
    { key: 'fluid_type', unit: '',    type: 'text',   placeholder: true }
  ],
  pneumatic: [
    { key: 'pressure',  unit: 'psi', type: 'number', step: '0.1'   },
    { key: 'flow_rate', unit: 'cfm', type: 'number', step: '0.1'   },
    { key: 'diameter',  unit: 'in',  type: 'number', step: '0.125' }
  ],
  structural: [
    { key: 'load_capacity', unit: 'tons', type: 'number', step: '0.1' },
    { key: 'material',      unit: '',     type: 'text',   placeholder: true }
  ],
  data: [
    { key: 'bandwidth',      unit: 'Mbps', type: 'number', step: '1' },
    { key: 'protocol',       unit: '',     type: 'text',   placeholder: true },
    { key: 'connector_type', unit: '',     type: 'text',   placeholder: true }
  ],
  mechanical: [
    { key: 'torque', unit: 'Nm',  type: 'number', step: '1'   },
    { key: 'rpm',    unit: 'RPM', type: 'number', step: '1'   },
    { key: 'power',  unit: 'kW',  type: 'number', step: '0.1' }
  ]
}

export default class extends Controller {
  static targets = ["graph", "searchInput", "searchResults", "childList", "childCount", "childCountBadge", "childItems", "emptyState", "statusBanner", "componentsPanel", "graphPanel", "mainCard", "splitLayout", "assemblyTitle", "assemblyNameInput", "renameBtn", "nodeDetailPanel"]
  static values = {
    assemblyId: Number,
    // Legacy — kept so old asset-based views don't break while being phased out
    assetId: Number,
    hierarchyUrl: String,
    addChildUrl: String,
    removeChildUrl: String,
    updateEdgeUrl: String,
    updateNodeUrl: String,
    renameUrl: String,
    batchUpdateUrl: String,
    searchUrl: String,
    companyAssetsUrl: String,
    quickCreateUrl: String
  }

  connect() {
    this._collapsedNodes = new Set()
    this._multiSelected = new Map()
    this._isFullscreen = false
    this._connectMode = false
    this._connectSource = null
    this.initGraph()
    this.loadHierarchyData()
    this.setupEdgeModal()
    this.setupGlobalDismiss()
    this._setupMultiSelectBar()
  }

  disconnect() {
    if (this._isFullscreen) this._exitFullscreen()
    if (this.cy) this.cy.destroy()
    this.hideContextMenu()
    if (this._escapeHandler) document.removeEventListener('keydown', this._escapeHandler)
    if (this._dismissHandler) document.removeEventListener('click', this._dismissHandler)
  }

  // ── Inline assembly rename ─────────────────────────────────────────────────

  startRename() {
    const title = this.assemblyTitleTarget
    const input = this.assemblyNameInputTarget
    input.value = title.textContent.trim()
    title.classList.add('hidden')
    input.classList.remove('hidden')
    this.renameBtnTarget.classList.add('hidden')
    input.focus()
    input.select()
  }

  handleRenameKey(event) {
    if (event.key === 'Enter') { event.preventDefault(); this.saveRename() }
    if (event.key === 'Escape') { this.cancelRename() }
  }

  cancelRename() {
    const title = this.assemblyTitleTarget
    const input = this.assemblyNameInputTarget
    input.classList.add('hidden')
    title.classList.remove('hidden')
    this.renameBtnTarget.classList.remove('hidden')
  }

  async saveRename() {
    const input = this.assemblyNameInputTarget
    // Guard: if input is already hidden (e.g. Escape already ran) do nothing
    if (input.classList.contains('hidden')) return

    const newName = input.value.trim()
    const title = this.assemblyTitleTarget
    const oldName = title.textContent.trim()

    try {
      const response = await fetch(this.renameUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ assembly_name: newName })
      })
      const data = await response.json()
      if (data.success) {
        title.textContent = data.display_name
        input.value = data.display_name
        // Update breadcrumb/page title if present
        // Swap the old name for the new one (locale-independent; the title prefix is translated server-side)
        if (oldName && document.title.includes(oldName)) {
          document.title = document.title.replace(oldName, data.display_name)
        }
      } else {
        console.error('Rename failed:', data.error)
      }
    } catch (e) {
      console.error('Rename error:', e)
    } finally {
      this.cancelRename()
    }
  }

  // ── Highlight a node in the graph (pan + flash) ────────────────────────────

  highlightNode(nodeId) {
    if (!this.cy || !nodeId) return
    const node = this.cy.getElementById(String(nodeId))
    if (!node || !node.length) return

    this._selectedNode?.unselect()
    this._selectedNode = node
    this.cy.elements().unselect()
    node.select()

    this.cy.animate({
      center: { eles: node },
      zoom: Math.max(this.cy.zoom(), 1.0)
    }, { duration: 400 })

    node.addClass('highlight-flash')
    setTimeout(() => node.removeClass('highlight-flash'), 1200)
  }

  initGraph() {
    const container = this.graphTarget

    this.cy = cytoscape({
      container: container,
      wheelSensitivity: 0.3,
      minZoom: 0.3,
      maxZoom: 3,
      style: [
        // Node base style
        {
          selector: 'node',
          style: {
            'label': 'data(label)',
            'text-valign': 'bottom',
            'text-halign': 'center',
            'text-margin-y': 10,
            'font-size': '13px',
            'font-weight': '600',
            'font-family': 'Inter, system-ui, sans-serif',
            'color': '#e2e8f0',
            'text-outline-color': '#0f172a',
            'text-outline-width': 2,
            'background-color': '#3b82f6',
            'border-width': 3,
            'border-color': '#60a5fa',
            'width': 60,
            'height': 60,
            'shape': 'round-rectangle',
            'corner-radius': 12,
            'overlay-padding': 8,
            'text-max-width': '110px',
            'text-wrap': 'wrap',
            'transition-property': 'background-color, border-color, width, height',
            'transition-duration': '0.2s'
          }
        },
        // Root node (mother component)
        {
          selector: 'node[?is_root]',
          style: {
            'background-color': '#8b5cf6',
            'border-color': '#a78bfa',
            'border-width': 4,
            'width': 80,
            'height': 80,
            'font-size': '15px',
            'font-weight': '700',
            'shape': 'round-hexagon'
          }
        },
        // Sub-component nodes (have a parent) — dashed red border
        {
          selector: 'node[!is_root]',
          style: {
            'border-style': 'dashed',
            'border-color': '#ef4444',
            'border-width': 3
          }
        },
        // Nodes with children (intermediate assemblies)
        {
          selector: 'node[child_count > 0][!is_root]',
          style: {
            'background-color': '#0ea5e9',
            'border-color': '#38bdf8',
            'width': 70,
            'height': 70,
            'shape': 'round-diamond'
          }
        },
        // Status-based colors
        {
          selector: 'node[status = "active"]',
          style: {
            'background-opacity': 1
          }
        },
        {
          selector: 'node[status = "maintenance"]',
          style: {
            'background-color': '#f59e0b',
            'border-color': '#fbbf24'
          }
        },
        {
          selector: 'node[status = "retired"]',
          style: {
            'background-color': '#6b7280',
            'border-color': '#9ca3af'
          }
        },
        {
          selector: 'node[status = "missing"]',
          style: {
            'background-color': '#ef4444',
            'border-color': '#f87171'
          }
        },
        {
          selector: 'node[status = "emergency"]',
          style: {
            'background-color': '#dc2626',
            'border-color': '#fca5a5',
            'border-width': 5,
            'border-style': 'double'
          }
        },
        {
          selector: 'node[status = "breakdown"]',
          style: {
            'background-color': '#991b1b',
            'border-color': '#ef4444',
            'border-width': 4,
            'border-style': 'dashed'
          }
        },
        // Connect mode source node — pulsing green ring
        {
          selector: 'node.connect-source',
          style: {
            'border-width': 5,
            'border-color': '#22c55e',
            'border-style': 'solid',
            'background-color': '#dcfce7'
          }
        },
        // Backup indicator (node that has a backup)
        {
          selector: 'node[?has_backup]',
          style: {
            'background-image-opacity': 0.15,
            'border-width': 4
          }
        },
        // Selected node
        {
          selector: 'node:selected',
          style: {
            'border-color': '#fbbf24',
            'border-width': 5,
            'background-color': '#f59e0b'
          }
        },
        // Hover effect
        {
          selector: 'node:active',
          style: {
            'overlay-color': '#fbbf24',
            'overlay-opacity': 0.2
          }
        },
        // Edge style — hierarchy edges
        {
          selector: 'edge',
          style: {
            'width': 3,
            'line-color': '#475569',
            'target-arrow-color': '#94a3b8',
            'target-arrow-shape': 'triangle',
            'curve-style': 'bezier',
            'arrow-scale': 1.2,
            'line-opacity': 0.8,
            'transition-property': 'line-color, width',
            'transition-duration': '0.2s'
          }
        },
        // Edge with label (connection type or quantity)
        {
          selector: 'edge[label]',
          style: {
            'label': 'data(label)',
            'text-rotation': 'autorotate',
            'font-size': '10px',
            'font-weight': '600',
            'color': '#94a3b8',
            'text-outline-color': '#0f172a',
            'text-outline-width': 2,
            'text-background-color': '#1e293b',
            'text-background-opacity': 0.9,
            'text-background-padding': 4,
            'text-background-shape': 'roundrectangle',
            'text-max-width': '120px',
            'text-wrap': 'ellipsis'
          }
        },
        // Edge with quantity label (fallback for edges without connection_type)
        {
          selector: 'edge[quantity > 1][!label]',
          style: {
            'label': 'data(quantity)',
            'text-rotation': 'autorotate',
            'font-size': '11px',
            'font-weight': '700',
            'color': '#fbbf24',
            'text-outline-color': '#0f172a',
            'text-outline-width': 2,
            'text-background-color': '#1e293b',
            'text-background-opacity': 0.9,
            'text-background-padding': 4,
            'text-background-shape': 'roundrectangle'
          }
        },
        // Connection type: pipe
        {
          selector: 'edge[connection_type = "pipe"]',
          style: {
            'line-color': '#3b82f6',
            'target-arrow-color': '#60a5fa',
            'width': 4,
            'line-style': 'solid'
          }
        },
        // Connection type: electrical
        {
          selector: 'edge[connection_type = "electrical"]',
          style: {
            'line-color': '#eab308',
            'target-arrow-color': '#fbbf24',
            'width': 3,
            'line-style': 'dashed'
          }
        },
        // Connection type: hydraulic
        {
          selector: 'edge[connection_type = "hydraulic"]',
          style: {
            'line-color': '#ef4444',
            'target-arrow-color': '#f87171',
            'width': 4,
            'line-style': 'solid'
          }
        },
        // Connection type: pneumatic
        {
          selector: 'edge[connection_type = "pneumatic"]',
          style: {
            'line-color': '#06b6d4',
            'target-arrow-color': '#22d3ee',
            'width': 3,
            'line-style': 'dotted'
          }
        },
        // Connection type: structural
        {
          selector: 'edge[connection_type = "structural"]',
          style: {
            'line-color': '#78716c',
            'target-arrow-color': '#a8a29e',
            'width': 5,
            'line-style': 'solid'
          }
        },
        // Connection type: data
        {
          selector: 'edge[connection_type = "data"]',
          style: {
            'line-color': '#a855f7',
            'target-arrow-color': '#c084fc',
            'width': 2,
            'line-style': 'dashed'
          }
        },
        // Connection type: mechanical
        {
          selector: 'edge[connection_type = "mechanical"]',
          style: {
            'line-color': '#84cc16',
            'target-arrow-color': '#a3e635',
            'width': 3,
            'line-style': 'solid'
          }
        },
        // Selected edge
        {
          selector: 'edge:selected',
          style: {
            'line-color': '#fbbf24',
            'target-arrow-color': '#fbbf24',
            'width': 5
          }
        },
        // Collapsed node — subtree hidden, node gets a folded look
        {
          selector: 'node.collapsed',
          style: {
            'border-width': 4,
            'border-style': 'dashed',
            'border-color': '#818cf8',
            'background-color': '#eef2ff',
            'color': '#4338ca',
            'font-weight': 'bold'
          }
        },
        // Selected node — prominent ring
        {
          selector: 'node:selected',
          style: {
            'border-width': 3,
            'border-color': '#7c3aed',
            'border-style': 'solid'
          }
        },
        // Temporary highlight flash (e.g. when clicking backup/parent link)
        {
          selector: 'node.highlight-flash',
          style: {
            'border-width': 4,
            'border-color': '#7c3aed',
            'background-color': '#ede9fe'
          }
        },
        // Multi-selected nodes (shift+click or box select)
        {
          selector: 'node.multi-selected',
          style: {
            'border-width': 3,
            'border-color': '#f59e0b',
            'border-style': 'solid',
            'background-color': '#fffbeb'
          }
        },
        // Backup relationship edge (visual only, not a hierarchy edge)
        {
          selector: 'edge.backup-edge',
          style: {
            'line-color': '#f97316',
            'target-arrow-color': '#f97316',
            'target-arrow-shape': 'triangle',
            'width': 2,
            'line-style': 'dashed',
            'curve-style': 'unbundled-bezier',
            'control-point-distances': [60],
            'control-point-weights': [0.5],
            'label': t('javascript.controllers_v2.assembly_editor_controller.backup_edge_label'),
            'font-size': '9px',
            'color': '#f97316',
            'font-weight': '600',
            'text-outline-color': '#0f172a',
            'text-outline-width': 2,
            'text-background-color': '#1e293b',
            'text-background-opacity': 0.9,
            'text-background-padding': 3,
            'text-background-shape': 'roundrectangle',
            'opacity': 0.85
          }
        }
      ],
      layout: { name: 'preset' },
      boxSelectionEnabled: true
    })

    // Node click handler - shift+click for multi-select, plain click for detail panel
    // In connect mode: first click = source, second click = make target a child of source
    this.cy.on('tap', 'node', (evt) => {
      const node = evt.target
      if (this._connectMode) {
        evt.stopPropagation()
        if (!this._connectSource) {
          this._connectSource = node
          node.addClass('connect-source')
          this._setConnectHint(t('javascript.controllers_v2.assembly_editor_controller.connect_hint_target'))
        } else if (node.id() === this._connectSource.id()) {
          this.exitConnectMode()
        } else {
          const parentId = this._connectSource.id()
          const childId = node.id()
          this.exitConnectMode()
          this.reparentNode(childId, parentId)
        }
        return
      }
      if (evt.originalEvent?.shiftKey) {
        const id = String(node.id())
        if (this._multiSelected.has(id)) {
          this._multiSelected.delete(id)
          node.removeClass('multi-selected')
        } else {
          this._multiSelected.set(id, node)
          node.addClass('multi-selected')
        }
        this._syncMultiBar()
      } else {
        this._clearMultiSelected()
        this._selectedNode?.unselect()
        this._selectedNode = node
        node.select()
        this.showNodeDetails(node.data())
      }
    })

    // Box select - add all grabbed nodes to multi-select
    this.cy.on('boxselect', 'node', (evt) => {
      const node = evt.target
      const id = String(node.id())
      this._multiSelected.set(id, node)
      node.addClass('multi-selected')
      node.unselect()
    })

    this.cy.on('boxend', () => {
      if (this._multiSelected.size > 0) this._syncMultiBar()
    })

    // Edge click handler - open edge edit modal
    this.cy.on('tap', 'edge', (evt) => {
      evt.stopPropagation()
      this.openEdgeModal(evt.target.data())
    })

    // Background click - deselect + hide overlays
    this.cy.on('tap', (evt) => {
      if (evt.target === this.cy) {
        this._selectedNode?.unselect()
        this._selectedNode = null
        this._clearMultiSelected()
        this.hideNodeDetails()
        this.closeEdgeModal()
      }
    })

    // Right-click node - status context menu
    this.cy.on('cxttap', 'node', (evt) => {
      evt.stopPropagation()
      const pos = evt.renderedPosition
      const container = this.graphTarget.getBoundingClientRect()
      this.showContextMenu(evt.target.data(), {
        x: container.left + pos.x,
        y: container.top + pos.y
      })
    })

    // Dynamic label visibility based on zoom level to prevent text collision at scale
    this.cy.on('zoom', () => {
      this.updateLabelsForZoom()
    })
  }

  // ═══════════════════════════════════════════════════
  // EDGE EDIT MODAL
  // ═══════════════════════════════════════════════════

  setupEdgeModal() {
    const modal = document.getElementById('edge-edit-modal')
    const card = document.getElementById('edge-modal-card')
    if (!modal) return

    modal.addEventListener('click', (e) => { if (e.target === modal) this.closeEdgeModal() })
    card?.addEventListener('click', (e) => e.stopPropagation())

    document.getElementById('edge-modal-close')?.addEventListener('click', () => this.closeEdgeModal())
    document.getElementById('edge-modal-cancel')?.addEventListener('click', () => this.closeEdgeModal())
    document.getElementById('edge-modal-save')?.addEventListener('click', () => this.saveEdgeMetadata())
    document.getElementById('edge-connection-type')?.addEventListener('change', (e) => {
      this.renderMetadataFields(e.target.value, this._editingEdgeMeta || {})
    })
  }

  setupGlobalDismiss() {
    this._escapeHandler = (e) => {
      if (e.key === 'Escape') {
        if (this._connectMode) { this.exitConnectMode(); return }
        this.closeEdgeModal()
        this.hideContextMenu()
        this.hideNodeDetails()
        if (this._isFullscreen) this.fullscreenToggle()
      }
    }
    this._dismissHandler = () => this.hideContextMenu()
    document.addEventListener('keydown', this._escapeHandler)
    document.addEventListener('click', this._dismissHandler)
  }

  openEdgeModal(data) {
    this._editingEdgeChildId = data.target
    this._editingEdgeId = data.id
    this._editingEdgeMeta = data.connection_metadata || {}

    const relationshipSelect = document.getElementById('edge-relationship-type')
    if (relationshipSelect) relationshipSelect.value = data.relationship_type || ''

    const typeSelect = document.getElementById('edge-connection-type')
    if (typeSelect) typeSelect.value = data.connection_type || ''

    const notesField = document.getElementById('edge-connection-notes')
    if (notesField) notesField.value = data.connection_notes || ''

    this.renderMetadataFields(data.connection_type || '', this._editingEdgeMeta)

    document.getElementById('edge-edit-modal')?.classList.remove('hidden')
  }

  closeEdgeModal() {
    document.getElementById('edge-edit-modal')?.classList.add('hidden')
    this._editingEdgeChildId = null
    this._editingEdgeId = null
    this._editingEdgeMeta = {}
  }

  // Render dynamic metadata fields for the selected connection type
  renderMetadataFields(type, meta = {}) {
    const container = document.getElementById('edge-metadata-fields')
    if (!container) return

    const fields = METADATA_FIELDS[type] || []
    if (fields.length === 0) {
      container.innerHTML = type
        ? `<p class="text-xs text-gray-400 text-center py-1">${t("javascript.controllers_v2.assembly_editor_controller.no_metadata_fields")}</p>`
        : ''
      return
    }

    const inputClass = 'w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/60'

    container.innerHTML = fields.map(f => {
      const value = (meta[f.key] != null) ? this.escapeHtml(String(meta[f.key])) : ''
      const label = t(`${ASSEMBLY_I18N}.metadata.${type}.${f.key}`)
      const placeholder = f.placeholder
        ? t(`${ASSEMBLY_I18N}.metadata.${type}.${f.key}_placeholder`)
        : (f.type === 'number' ? '0' : '…')
      const unitLabel = f.unit ? ` <span class="font-normal text-gray-400">(${f.unit})</span>` : ''
      return `
        <div>
          <label class="block text-xs font-semibold text-gray-500 mb-1">${this.escapeHtml(label)}${unitLabel}</label>
          <input data-meta-key="${f.key}"
                 type="${f.type}"
                 ${f.type === 'number' ? 'min="0"' : ''}
                 ${f.step ? `step="${f.step}"` : ''}
                 value="${value}"
                 placeholder="${this.escapeHtml(placeholder)}"
                 class="${inputClass}">
        </div>`
    }).join('')
  }

  // Read all rendered metadata fields into an object
  getMetadataValues() {
    const container = document.getElementById('edge-metadata-fields')
    if (!container) return {}
    const result = {}
    container.querySelectorAll('[data-meta-key]').forEach(input => {
      result[input.dataset.metaKey] = input.value.trim() || null
    })
    return result
  }

  async saveEdgeMetadata() {
    if (!this._editingEdgeChildId) return
    const relationshipType = document.getElementById('edge-relationship-type')?.value || ''
    const connectionType = document.getElementById('edge-connection-type')?.value || ''
    const connectionNotes = document.getElementById('edge-connection-notes')?.value || ''

    try {
      const response = await fetch(this.updateEdgeUrlValue, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken },
        body: JSON.stringify({
          node_id: this._editingEdgeChildId,
          relationship_type: relationshipType || null,
          connection_type: connectionType || null,
          connection_metadata: this.getMetadataValues(),
          connection_notes: connectionNotes
        })
      })

      const data = await response.json()
      if (response.ok) {
        const edge = this.cy.getElementById(this._editingEdgeId)
        if (edge.length) {
          const newLabel = this.buildEdgeLabel(data.connection_type, data.connection_metadata)
          edge.data('relationship_type', data.relationship_type || '')
          edge.data('connection_type', data.connection_type || '')
          edge.data('connection_metadata', data.connection_metadata || {})
          edge.data('connection_notes', data.connection_notes || '')
          edge.data('label', newLabel || null)
        }
        const childNode = this.cy.getElementById(String(this._editingEdgeChildId))
        if (childNode.length) childNode.data('relationship_type', data.relationship_type || '')
        this.closeEdgeModal()
        this.flashMessage(t('javascript.controllers_v2.assembly_editor_controller.connection_updated'), 'success')
      } else {
        this.flashMessage(data.error || t('javascript.controllers_v2.assembly_editor_controller.failed_to_update_connection'), 'error')
      }
    } catch (error) {
      console.error('Failed to save edge:', error)
      this.flashMessage(t('javascript.controllers_v2.assembly_editor_controller.network_error'), 'error')
    }
  }

  // Build edge label — mirrors Ruby Asset#build_edge_label
  buildEdgeLabel(connectionType, metadata) {
    if (!connectionType) return ''
    const parts = [this._connectionTypeLabel(connectionType)]
    const m = metadata || {}
    switch (connectionType) {
      case 'pipe': case 'hydraulic':
        if (m.pressure)  parts.push(`${m.pressure} psi`)
        if (m.flow_rate) parts.push(`${m.flow_rate} gpm`)
        if (m.diameter)  parts.push(`${m.diameter}in`)
        break
      case 'pneumatic':
        if (m.pressure)  parts.push(`${m.pressure} psi`)
        if (m.flow_rate) parts.push(`${m.flow_rate} cfm`)
        break
      case 'electrical':
        if (m.voltage)   parts.push(`${m.voltage}V`)
        if (m.current)   parts.push(`${m.current}A`)
        if (m.power)     parts.push(`${m.power} kW`)
        break
      case 'structural':
        if (m.load_capacity) parts.push(`${m.load_capacity}t`)
        if (m.material)      parts.push(m.material)
        break
      case 'data':
        if (m.bandwidth) parts.push(`${m.bandwidth} Mbps`)
        if (m.protocol)  parts.push(m.protocol)
        break
      case 'mechanical':
        if (m.torque) parts.push(`${m.torque} Nm`)
        if (m.rpm)    parts.push(`${m.rpm} RPM`)
        if (m.power)  parts.push(`${m.power} kW`)
        break
    }
    return parts.join(' · ')
  }

  // ═══════════════════════════════════════════════════
  // RIGHT-CLICK CONTEXT MENU (status quick-set)
  // ═══════════════════════════════════════════════════

  showContextMenu(nodeData, screenPos) {
    this.hideContextMenu()

    const statuses = ['active', 'maintenance', 'emergency', 'breakdown', 'missing', 'retired', 'inactive']
    const dotColors = {
      active: 'background:#22c55e', maintenance: 'background:#f59e0b', missing: 'background:#ef4444',
      emergency: 'background:#dc2626', breakdown: 'background:#991b1b', retired: 'background:#9ca3af',
      inactive: 'background:#9ca3af'
    }

    const hasChildren = (nodeData.child_count || 0) > 0
    const isCollapsed = this._collapsedNodes.has(String(nodeData.id))

    const hasParent = !!this.cy?.getElementById(String(nodeData.id))?.data('parent_node_id')

    const menu = document.createElement('div')
    menu.id = 'node-context-menu'
    menu.className = 'fixed z-50 w-56 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden py-1'
    menu.style.cssText = `left:${screenPos.x}px;top:${screenPos.y}px`
    menu.innerHTML = `
      <p class="px-3 pt-1.5 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">${t("javascript.controllers_v2.assembly_editor_controller.actions")}</p>
      <button id="ctx-connect-btn" class="w-full flex items-center gap-2.5 px-3 py-1.5 hover:bg-green-50 text-left text-sm text-green-700 transition-colors">
        <i data-lucide="link" class="w-4 h-4"></i>
        ${t("javascript.controllers_v2.assembly_editor_controller.connect_from_here_ellipsis")}
      </button>
      ${hasParent ? `
        <button id="ctx-detach-btn" class="w-full flex items-center gap-2.5 px-3 py-1.5 hover:bg-amber-50 text-left text-sm text-amber-700 transition-colors">
          <i data-lucide="scissors" class="w-4 h-4"></i>
          ${t("javascript.controllers_v2.assembly_editor_controller.detach_from_parent")}
        </button>
      ` : ''}
      <button id="ctx-remove-btn" class="w-full flex items-center gap-2.5 px-3 py-1.5 hover:bg-red-50 text-left text-sm text-red-600 transition-colors">
        <i data-lucide="trash-2" class="w-4 h-4"></i>
        ${t("javascript.controllers_v2.assembly_editor_controller.remove_from_assembly")}
      </button>
      <div class="mx-3 my-1 border-t border-gray-100"></div>
      ${hasChildren ? `
        <p class="px-3 pt-0.5 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">${t("javascript.controllers_v2.assembly_editor_controller.subtree")}</p>
        <button id="ctx-collapse-btn" class="w-full flex items-center gap-2.5 px-3 py-1.5 hover:bg-indigo-50 text-left text-sm text-indigo-600 transition-colors">
          <i data-lucide="${isCollapsed ? 'eye' : 'eye-off'}" class="w-4 h-4"></i>
          ${isCollapsed ? t("javascript.controllers_v2.assembly_editor_controller.expand_children") : t("javascript.controllers_v2.assembly_editor_controller.collapse_children")}
        </button>
        <div class="mx-3 my-1 border-t border-gray-100"></div>
      ` : ''}
      <p class="px-3 pt-0.5 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">${t("javascript.controllers_v2.assembly_editor_controller.set_status")}</p>
      ${statuses.map(s => `
        <button class="w-full flex items-center gap-2.5 px-3 py-1.5 hover:bg-gray-50 text-left text-sm text-gray-700 capitalize transition-colors"
                data-status="${s}">
          <div class="w-2 h-2 rounded-full flex-shrink-0" style="${dotColors[s] || 'background:#9ca3af'}"></div>
          ${this.escapeHtml(this._statusLabel(s))}
        </button>
      `).join('')}
    `
    document.body.appendChild(menu)
    createIcons({ icons: ASSEMBLY_ICONS, root: menu })

    menu.querySelector('#ctx-connect-btn')?.addEventListener('click', (e) => {
      e.stopPropagation()
      this.hideContextMenu()
      this.enterConnectMode(String(nodeData.id))
    })

    menu.querySelector('#ctx-detach-btn')?.addEventListener('click', (e) => {
      e.stopPropagation()
      this.hideContextMenu()
      this.detachNode(String(nodeData.id))
    })

    menu.querySelector('#ctx-remove-btn')?.addEventListener('click', (e) => {
      e.stopPropagation()
      this.hideContextMenu()
      this.removeNodeById(String(nodeData.id))
    })

    menu.querySelector('#ctx-collapse-btn')?.addEventListener('click', (e) => {
      e.stopPropagation()
      this.toggleCollapse(nodeData.id)
      this.hideContextMenu()
    })

    menu.querySelectorAll('[data-status]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        this.setNodeStatus(nodeData.id, btn.dataset.status)
        this.hideContextMenu()
      })
    })

    // Keep menu within viewport
    requestAnimationFrame(() => {
      const rect = menu.getBoundingClientRect()
      if (rect.right > window.innerWidth) menu.style.left = `${screenPos.x - rect.width}px`
      if (rect.bottom > window.innerHeight) menu.style.top = `${screenPos.y - rect.height}px`
    })
  }

  hideContextMenu() {
    document.getElementById('node-context-menu')?.remove()
  }

  // ═══════════════════════════════════════════════════
  // COLLAPSE / EXPAND SUBTREES
  // ═══════════════════════════════════════════════════

  // Called from sidebar eye button (data-action)
  toggleCollapseFromSidebar(event) {
    event.stopPropagation()
    const nodeId = String(event.currentTarget.dataset.nodeId)
    this.toggleCollapse(nodeId)
  }

  toggleCollapse(nodeId) {
    const nodeIdStr = String(nodeId)
    const node = this.cy?.getElementById(nodeIdStr)
    if (!node?.length) return

    if (this._collapsedNodes.has(nodeIdStr)) {
      this._collapsedNodes.delete(nodeIdStr)
      node.removeClass('collapsed')
      node.data('label', node.data('_origLabel') || node.data('label'))
      node.removeData('_origLabel')
    } else {
      this._collapsedNodes.add(nodeIdStr)
      const origLabel = node.data('label')
      const hiddenCount = node.successors('node').length
      node.data('_origLabel', origLabel)
      node.data('label', `${origLabel}\n▶ ${hiddenCount}`)
      node.addClass('collapsed')
    }
    this._applyCollapsedState()
    this._updateSidebarCollapseBtn(nodeIdStr)

    // Refresh detail panel collapse button if open
    if (this._currentNodeId === nodeIdStr) {
      const btn = document.getElementById('detail-collapse-btn')
      if (btn) this._renderCollapseBtn(btn, nodeIdStr)
    }
  }

  _applyCollapsedState() {
    if (!this.cy) return
    this.cy.elements().style('display', 'element')
    this._collapsedNodes.forEach(nodeId => {
      const node = this.cy.getElementById(nodeId)
      if (node.length) node.successors().style('display', 'none')
    })
  }

  _updateSidebarCollapseBtn(nodeId) {
    const card = document.querySelector(`[data-child-card="${nodeId}"]`)
    const btn = card?.querySelector('.collapse-toggle-btn')
    if (!btn) return
    this._renderCollapseBtn(btn, nodeId)
  }

  _renderCollapseBtn(btn, nodeId) {
    const isCollapsed = this._collapsedNodes.has(String(nodeId))
    btn.innerHTML = `<i data-lucide="${isCollapsed ? 'eye-off' : 'eye'}" class="w-4 h-4"></i>`
    btn.title = isCollapsed ? t('javascript.controllers_v2.assembly_editor_controller.show_in_graph') : t('javascript.controllers_v2.assembly_editor_controller.hide_from_graph')
    createIcons({ icons: ASSEMBLY_ICONS, root: btn })
  }

  // ═══════════════════════════════════════════════════
  // FULLSCREEN
  // ═══════════════════════════════════════════════════

  fullscreenToggle() {
    if (this._isFullscreen) {
      this._exitFullscreen()
    } else {
      this._enterFullscreen()
    }
  }

  _enterFullscreen() {
    this._isFullscreen = true
    const el = this.element
    el.style.cssText = 'position:fixed;inset:0;z-index:9998;background:#fff;overflow-y:auto;padding:1rem;'
    document.body.style.overflow = 'hidden'
    if (this.hasSplitLayoutTarget) {
      this._origSplitHeight = this.splitLayoutTarget.style.height
      this.splitLayoutTarget.style.height = 'calc(100vh - 160px)'
    }
    const btn = document.getElementById('fullscreen-btn')
    if (btn) {
      btn.innerHTML = `<i data-lucide="minimize-2" class="w-4 h-4"></i><span class="hidden sm:inline ml-1.5">${t("javascript.controllers_v2.assembly_editor_controller.exit_fullscreen")}</span>`
      createIcons({ icons: ASSEMBLY_ICONS, root: btn })
    }
    setTimeout(() => { if (this.cy) { this.cy.resize(); this.cy.fit(50) } }, 80)
  }

  _exitFullscreen() {
    this._isFullscreen = false
    this.element.style.cssText = ''
    document.body.style.overflow = ''
    if (this.hasSplitLayoutTarget) {
      this.splitLayoutTarget.style.height = this._origSplitHeight || ''
    }
    const btn = document.getElementById('fullscreen-btn')
    if (btn) {
      btn.innerHTML = `<i data-lucide="maximize-2" class="w-4 h-4"></i><span class="hidden sm:inline ml-1.5">${t("javascript.controllers_v2.assembly_editor_controller.fullscreen")}</span>`
      createIcons({ icons: ASSEMBLY_ICONS, root: btn })
    }
    setTimeout(() => { if (this.cy) { this.cy.resize(); this.cy.fit(50) } }, 80)
  }


  async setNodeStatus(nodeId, status) {
    try {
      const response = await fetch(this.updateNodeUrlValue, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken },
        body: JSON.stringify({ node_id: nodeId, status })
      })
      const data = await response.json()
      if (response.ok) {
        const node = this.cy.getElementById(String(nodeId))
        if (node.length) node.data('status', data.status)

        // Refresh detail panel if it's showing this node
        if (this._currentNodeId === String(nodeId)) {
          const panel = document.getElementById('node-detail-panel')
          if (panel && !panel.classList.contains('hidden')) {
            const statusColors = {
              active: 'bg-green-100 text-green-700 border-green-200',
              maintenance: 'bg-amber-100 text-amber-700 border-amber-200',
              retired: 'bg-gray-100 text-gray-500 border-gray-200',
              missing: 'bg-red-100 text-red-700 border-red-200',
              inactive: 'bg-gray-100 text-gray-500 border-gray-200',
              emergency: 'bg-red-200 text-red-900 border-red-300',
              breakdown: 'bg-red-100 text-red-800 border-red-200'
            }
            const badge = panel.querySelector('.status-badge')
            if (badge) {
              badge.className = `status-badge inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border mt-1 ${statusColors[data.status] || statusColors.active}`
              badge.textContent = this._statusLabel(data.status)
            }
            const bannerWrapper = panel.querySelector('.alert-banner-wrapper')
            if (bannerWrapper) {
              bannerWrapper.innerHTML = this.buildAlertBanner(data.status)
              createIcons({ icons: ASSEMBLY_ICONS, root: bannerWrapper })
            }
          }
        }
        this.flashMessage(t('javascript.controllers_v2.assembly_editor_controller.status_changed', { status: this._statusLabel(status) }), 'success')
      } else {
        this.flashMessage(data.error || t('javascript.controllers_v2.assembly_editor_controller.failed_to_update_status'), 'error')
      }
    } catch (err) {
      console.error(err)
      this.flashMessage(t('javascript.controllers_v2.assembly_editor_controller.network_error'), 'error')
    }
  }

  // Set the backup_for_id on nodeId (making it a backup for backupForId)
  async setBackupFor(nodeId, backupForId) {
    const res = await this._patchNode({ node_id: nodeId, backup_for_id: backupForId })
    if (res) {
      // Update this node (the backup)
      const node = this.cy.getElementById(String(nodeId))
      if (node.length) node.data('backup_for_id', res.backup_for_id || null)
      // Update the primary node's has_backup flag in the graph
      if (backupForId) {
        const primaryNode = this.cy.getElementById(String(backupForId))
        if (primaryNode.length) primaryNode.data('has_backup', true)
      }
      this.refreshDetailPanelBackup(nodeId, res)
      this._addBackupEdges()
      this.flashMessage(backupForId ? t('javascript.controllers_v2.assembly_editor_controller.backup_assignment_saved') : t('javascript.controllers_v2.assembly_editor_controller.backup_assignment_cleared'), 'success')
    }
  }

  // Assign/remove a backup UNIT for nodeId (nodeId is the primary)
  async setBackupUnit(nodeId, backupUnitId) {
    const res = await this._patchNode({ node_id: nodeId, backup_unit_id: backupUnitId })
    if (res) {
      // Update this node (the primary)
      const node = this.cy.getElementById(String(nodeId))
      if (node.length) node.data('has_backup', res.has_backup)
      // Update the backup node's backup_for_id in the graph
      if (backupUnitId) {
        const backupNode = this.cy.getElementById(String(backupUnitId))
        if (backupNode.length) backupNode.data('backup_for_id', String(nodeId))
      } else {
        // Clearing: find the old backup node and clear its link
        this.cy.nodes().forEach(n => {
          if (n.data('backup_for_id') === String(nodeId)) n.data('backup_for_id', null)
        })
      }
      this.refreshDetailPanelBackup(nodeId, res)
      this._addBackupEdges()
      this.flashMessage(backupUnitId ? t('javascript.controllers_v2.assembly_editor_controller.backup_unit_assigned') : t('javascript.controllers_v2.assembly_editor_controller.backup_unit_removed'), 'success')
    }
  }

  async _patchNode(payload) {
    try {
      const response = await fetch(this.updateNodeUrlValue, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken },
        body: JSON.stringify(payload)
      })
      const data = await response.json()
      if (!response.ok) { this.flashMessage(data.error || t('javascript.controllers_v2.assembly_editor_controller.update_failed'), 'error'); return null }
      return data
    } catch (err) {
      console.error(err); this.flashMessage(t('javascript.controllers_v2.assembly_editor_controller.network_error'), 'error'); return null
    }
  }

  refreshDetailPanelBackup(nodeId, serverData) {
    if (this._currentNodeId !== String(nodeId)) return
    const panel = document.getElementById('node-detail-panel')
    if (!panel || panel.classList.contains('hidden')) return
    const section = panel.querySelector('.backup-section-wrapper')
    if (section) {
      const cyNode = this.cy?.getElementById(String(nodeId))
      section.innerHTML = this.buildBackupSection(
        String(nodeId),
        serverData.has_backup,
        serverData.backup_for_id,
        cyNode?.data('backup_unit_id'),
        cyNode?.data('backup_unit_name'),
        cyNode?.data('backup_unit_node_id'),
        cyNode?.data('backup_for_node_id')
      )
      this.attachBackupListeners(panel, String(nodeId))
      createIcons({ icons: ASSEMBLY_ICONS, root: section })
      section.querySelectorAll('[data-highlight-node]').forEach(btn => {
        btn.addEventListener('click', (e) => { e.stopPropagation(); this.highlightNode(btn.dataset.highlightNode) })
      })
    }
  }

  // ═══════════════════════════════════════════════════
  // NODE DETAIL PANEL (status + bidirectional backup UI)
  // ═══════════════════════════════════════════════════

  showNodeDetails(data) {
    this._currentNodeId = String(data.id)
    const detailPanel = document.getElementById('node-detail-panel')
    if (!detailPanel) return

    const statusColors = {
      active:      'bg-green-100 text-green-700 border-green-200',
      maintenance: 'bg-amber-100 text-amber-700 border-amber-200',
      retired:     'bg-gray-100 text-gray-500 border-gray-200',
      missing:     'bg-red-100 text-red-700 border-red-200',
      inactive:    'bg-gray-100 text-gray-500 border-gray-200',
      emergency:   'bg-red-200 text-red-900 border-red-300',
      breakdown:   'bg-red-100 text-red-800 border-red-200'
    }
    const dotColors = {
      active: '#22c55e', maintenance: '#f59e0b', missing: '#ef4444',
      emergency: '#dc2626', breakdown: '#991b1b', retired: '#9ca3af', inactive: '#9ca3af'
    }

    const statusClass = statusColors[data.status] || statusColors.active
    // data.name is the clean display name; data.label may include "S/N: …" on a second line
    const label = this.escapeHtml(data.name || data.label)
    const status = this.escapeHtml(this._statusLabel(data.status))
    const assetType = this.escapeHtml(data.asset_type)
    const brand = this.escapeHtml(data.brand)
    const modelNumber = this.escapeHtml(data.model_number)
    const serialNumber = this.escapeHtml(data.serial_number)
    const location = this.escapeHtml(data.location)
    const officeLocation = this.escapeHtml(data.office_location)
    const purchaseDate = this.escapeHtml(data.purchase_date)
    const warrantyExpiry = this.escapeHtml(data.warranty_expiry_date)
    const nextMaintenance = this.escapeHtml(data.next_maintenance_date)
    const relationshipType = this.escapeHtml(this._relationshipLabel(data.relationship_type))
    const installedAt = this.escapeHtml(data.installed_at)
    const safeAssetId = encodeURIComponent(data.asset_id || data.id)
    const allStatuses = ['active', 'maintenance', 'emergency', 'breakdown', 'missing', 'retired', 'inactive']

    const warrantyClass = (() => {
      if (!data.warranty_expiry_date) return ''
      const days = (new Date(data.warranty_expiry_date) - new Date()) / 86400000
      if (days < 0) return 'text-red-600 font-semibold'
      if (days < 90) return 'text-amber-600 font-semibold'
      return ''
    })()

    const row = (lbl, value, extraClass = '') => value
      ? `<div class="flex justify-between items-center gap-2">
           <span class="text-xs text-gray-400 flex-shrink-0">${lbl}</span>
           <span class="text-xs font-semibold text-gray-700 text-right truncate ${extraClass}">${value}</span>
         </div>`
      : ''

    // Role badge
    const roleBadge = data.is_root
      ? `<span class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-100 text-indigo-700 border border-indigo-200">
           <i data-lucide="star" class="w-2.5 h-2.5"></i>&nbsp;${t("javascript.controllers_v2.assembly_editor_controller.root_assembly")}
         </span>`
      : (data.child_count > 0
          ? `<span class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary/10 text-primary border border-primary/20">
               <i data-lucide="git-branch" class="w-2.5 h-2.5"></i>&nbsp;${t("javascript.controllers_v2.assembly_editor_controller.sub_assembly")}
             </span>`
          : `<span class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-500 border border-gray-200">
               <i data-lucide="box" class="w-2.5 h-2.5"></i>&nbsp;${t("javascript.controllers_v2.assembly_editor_controller.component")}
             </span>`)

    // Parent/predecessor row — use parent_node_id (assembly_node ID) to look up in Cytoscape
    let parentHtml = ''
    if (data.parent_node_id) {
      const parentNode = this.cy?.getElementById(String(data.parent_node_id))
      const parentLabel = (parentNode?.length && parentNode.data('label'))
        ? this.escapeHtml(parentNode.data('label'))
        : t("javascript.controllers_v2.assembly_editor_controller.node_number", { id: data.parent_node_id })
      parentHtml = `
        <div class="pt-1.5 border-t border-gray-100">
          <div class="flex justify-between items-center gap-2">
            <span class="text-xs text-gray-400 flex-shrink-0">${relationshipType || t('javascript.controllers_v2.assembly_editor_controller.part_of')}</span>
            <button class="text-xs font-semibold text-indigo-600 hover:text-indigo-700 hover:underline truncate max-w-[130px] text-right transition-colors"
                    data-highlight-node="${data.parent_node_id}" title="${t("javascript.controllers_v2.assembly_editor_controller.highlight_in_graph")}">
              ${parentLabel}
            </button>
          </div>
        </div>`
    }

    // Connections: outgoing hierarchy edges with connection type or child names
    let connectionsHtml = ''
    let allConnItems = []
    if (this.cy) {
      const cyNode = this.cy.getElementById(String(data.id))
      const outEdges = cyNode?.connectedEdges().filter(e =>
        e.data('source') === String(data.id) && !e.hasClass('backup-edge')
      )
      if (outEdges && outEdges.length > 0) {
        const connTypeColors = {
          pipe: 'text-blue-600', electrical: 'text-yellow-600', hydraulic: 'text-red-600',
          pneumatic: 'text-cyan-600', structural: 'text-stone-600', data: 'text-purple-600', mechanical: 'text-green-600'
        }
        allConnItems = outEdges.map(e => {
          const targetId = e.data('target')
          const targetNode = this.cy.getElementById(targetId)
          const targetLabel = targetNode?.length ? this.escapeHtml(targetNode.data('name') || targetNode.data('label')) : t("javascript.controllers_v2.assembly_editor_controller.asset_number", { id: targetId })
          const targetType = targetNode?.length ? this.escapeHtml(targetNode.data('asset_type') || '') : ''
          const connType = e.data('connection_type')
          const relationship = this._relationshipLabel(e.data('relationship_type'))
          const connBadge = connType
            ? `<span class="text-[10px] font-semibold flex-shrink-0 ${connTypeColors[connType] || 'text-gray-500'}">${this.escapeHtml(this._connectionTypeLabel(connType))}</span>`
            : ''
          const relationshipBadge = relationship
            ? `<span class="text-[10px] font-semibold flex-shrink-0 text-violet-700 bg-violet-50 border border-violet-200 px-1 py-0.5 rounded">${this.escapeHtml(relationship)}</span>`
            : ''
          const isSubAssembly = targetNode?.length && (targetNode.data('child_count') || 0) > 0
          const statusDot = (() => {
            const s = targetNode?.data('status') || ''
            return { active: 'bg-green-400', maintenance: 'bg-amber-400', emergency: 'bg-red-500', breakdown: 'bg-red-800', retired: 'bg-gray-300' }[s] || 'bg-gray-300'
          })()
          return `<button class="flex items-center gap-2 w-full hover:bg-gray-50 rounded px-1.5 py-1 transition-colors text-left"
                          data-highlight-node="${targetId}" title="${this.escapeHtml(t("javascript.controllers_v2.assembly_editor_controller.highlight_named_in_graph", { name: targetLabel }))}">
            <div class="w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusDot}"></div>
            <span class="text-[11px] text-gray-700 font-medium truncate flex-1">${this.escapeHtml(targetLabel)}</span>
            ${targetType ? `<span class="text-[10px] text-gray-400 flex-shrink-0">${this.escapeHtml(targetType)}</span>` : ''}
            ${relationshipBadge}
            ${connBadge}
            ${isSubAssembly ? '<span class="text-[9px] font-bold text-primary bg-primary/10 px-1 py-0.5 rounded flex-shrink-0">' + this.escapeHtml(t('javascript.controllers_v2.assembly_editor_controller.sub_short')) + '</span>' : ''}
          </button>`
        })
        const FEEDS_LIMIT = 6
        const visibleItems = allConnItems.slice(0, FEEDS_LIMIT)
        const hiddenCount = allConnItems.length - FEEDS_LIMIT
        const showMoreBtn = hiddenCount > 0
          ? `<button id="feeds-show-more" class="text-[10px] text-primary hover:underline mt-0.5 pl-1">${t("javascript.controllers_v2.assembly_editor_controller.show_n_more", { count: hiddenCount })}</button>`
          : ''
        const sectionTitle = (data.child_count || 0) > 0 ? t('javascript.controllers_v2.assembly_editor_controller.contains') : t('javascript.controllers_v2.assembly_editor_controller.connections')
        connectionsHtml = `
          <div class="pt-1.5 border-t border-gray-100 space-y-0.5">
            <p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">${sectionTitle} <span class="font-normal text-gray-300">(${allConnItems.length})</span></p>
            <div id="feeds-list">${visibleItems.join('')}</div>
            ${showMoreBtn}
          </div>`
      }
    }

    detailPanel.innerHTML = `
      <div class="alert-banner-wrapper">${this.buildAlertBanner(data.status)}</div>
      <div class="px-4 py-3 border-b border-gray-100 flex items-start justify-between gap-2">
        <div class="min-w-0 flex-1">
          <h3 class="text-sm font-bold text-gray-900 truncate">${label}</h3>
          <div class="flex items-center gap-1.5 mt-1 flex-wrap">
            <span class="status-badge inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${statusClass}">${status}</span>
            <button id="detail-status-toggle" class="text-gray-300 hover:text-primary transition-colors" title="${t("javascript.controllers_v2.assembly_editor_controller.change_status")}">
              <i data-lucide="pencil" class="w-3 h-3"></i>
            </button>
            ${data.child_count > 0 ? `
            <button id="detail-collapse-btn" class="ml-1 text-gray-300 hover:text-indigo-500 transition-colors" title="${this._collapsedNodes.has(String(data.id)) ? t("javascript.controllers_v2.assembly_editor_controller.expand_subtree") : t("javascript.controllers_v2.assembly_editor_controller.collapse_subtree")}">
              <i data-lucide="${this._collapsedNodes.has(String(data.id)) ? 'eye' : 'eye-off'}" class="w-3 h-3"></i>
            </button>` : ''}
          </div>
          <div class="mt-1.5">${roleBadge}</div>
          <div id="detail-status-picker" class="hidden mt-2 grid grid-cols-2 gap-1">
            ${allStatuses.map(s => `
              <button class="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-gray-100 text-xs text-gray-700 capitalize transition-colors ${s === data.status ? 'bg-primary/10 text-primary font-semibold ring-1 ring-primary/20' : ''}"
                      data-status-pick="${s}">
                <div class="w-1.5 h-1.5 rounded-full flex-shrink-0" style="background:${dotColors[s] || '#9ca3af'}"></div>
                ${this.escapeHtml(this._statusLabel(s))}
              </button>
            `).join('')}
          </div>
        </div>
        <button id="detail-close-btn" class="text-gray-300 hover:text-gray-600 transition-colors flex-shrink-0 mt-0.5">
          <i data-lucide="x" class="w-4 h-4"></i>
        </button>
      </div>
      <div class="p-4 space-y-2 bg-gray-50/60 overflow-y-auto" style="max-height:calc(100% - 7rem)">

        ${(assetType || brand || modelNumber || serialNumber) ? `
        <div class="space-y-1.5">
          ${row(t('javascript.controllers_v2.assembly_editor_controller.type'), assetType)}
          ${row(t('javascript.controllers_v2.assembly_editor_controller.brand'), brand)}
          ${row(t('javascript.controllers_v2.assembly_editor_controller.model'), modelNumber)}
          ${serialNumber ? `<div class="flex justify-between items-center gap-2">
            <span class="text-xs text-gray-400 flex-shrink-0">${t("javascript.controllers_v2.assembly_editor_controller.serial_number")}</span>
            <span class="text-xs font-mono font-semibold text-gray-700 text-right truncate bg-gray-100 px-1.5 py-0.5 rounded">${serialNumber}</span>
          </div>` : ''}
        </div>` : ''}

        ${(location || officeLocation) ? `
        <div class="pt-1.5 border-t border-gray-100 space-y-1.5">
          ${officeLocation ? `<div class="flex items-start gap-2">
            <span class="text-xs text-gray-400 flex-shrink-0 mt-0.5">${t("javascript.controllers_v2.assembly_editor_controller.site")}</span>
            <span class="text-xs font-semibold text-gray-700 text-right leading-tight">${officeLocation}</span>
          </div>` : ''}
          ${location ? `<div class="flex items-start gap-2">
            <span class="text-xs text-gray-400 flex-shrink-0 mt-0.5">${t("javascript.controllers_v2.assembly_editor_controller.location")}</span>
            <span class="text-xs font-semibold text-gray-700 text-right leading-tight">${location}</span>
          </div>` : ''}
        </div>` : ''}

        ${(purchaseDate || warrantyExpiry || nextMaintenance || relationshipType || installedAt) ? `
        <div class="pt-1.5 border-t border-gray-100 space-y-1.5">
          ${row(t('javascript.controllers_v2.assembly_editor_controller.purchased'), purchaseDate)}
          ${warrantyExpiry ? `<div class="flex justify-between items-center gap-2">
            <span class="text-xs text-gray-400 flex-shrink-0">${t("javascript.controllers_v2.assembly_editor_controller.warranty")}</span>
            <span class="text-xs text-right ${warrantyClass || 'font-semibold text-gray-700'}">${warrantyExpiry}${warrantyClass.includes('red') ? ' ⚠️' : ''}</span>
          </div>` : ''}
          ${row(t('javascript.controllers_v2.assembly_editor_controller.next_pm'), nextMaintenance)}
          ${row(t('javascript.controllers_v2.assembly_editor_controller.relationship'), relationshipType)}
          ${row(t('javascript.controllers_v2.assembly_editor_controller.installed'), installedAt)}
        </div>` : ''}

        ${parentHtml}
        ${connectionsHtml}

        <div class="pt-1.5 border-t border-gray-100">
          <div class="backup-section-wrapper">${this.buildBackupSection(String(data.id), data.has_backup, data.backup_for_id, data.backup_unit_id, data.backup_unit_name, data.backup_unit_node_id, data.backup_for_node_id)}</div>
        </div>

        <div class="pt-1.5 border-t border-gray-100 space-y-1.5">
          <button id="detail-add-child-btn" class="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-semibold transition-colors w-full">
            <i data-lucide="circle-plus" class="w-3.5 h-3.5"></i>&nbsp;${t("javascript.controllers_v2.assembly_editor_controller.add_sub_component_here")}
          </button>
          <div id="detail-add-child-wrap" class="hidden space-y-1">
            <input id="detail-add-child-input" type="text" placeholder="${this.escapeHtml(t("javascript.controllers_v2.assembly_editor_controller.search_asset_to_add"))}"
                   class="w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary/30">
            <div id="detail-add-child-results" class="max-h-32 overflow-y-auto rounded-md border border-gray-100 bg-white shadow-sm empty:hidden"></div>
          </div>
        </div>

        <div class="pt-1 border-t border-gray-100 space-y-1.5">
          <div class="flex gap-1.5">
            <a href="${this.companyAssetsUrlValue}/${safeAssetId}/edit"
               target="_blank"
               class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-600 text-sm font-semibold transition-colors">
              <i data-lucide="pencil" class="w-4 h-4"></i>
              ${t("javascript.controllers_v2.assembly_editor_controller.edit_asset")}
            </a>
            <a href="${this.companyAssetsUrlValue}/${safeAssetId}"
               target="_blank"
               class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-primary hover:bg-primary/90 text-white text-sm font-semibold transition-colors">
              <i data-lucide="external-link" class="w-4 h-4"></i>
              ${t("javascript.controllers_v2.assembly_editor_controller.view_asset")}
            </a>
          </div>
          <div class="flex gap-1.5">
            <button id="detail-connect-btn"
               class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-green-300 bg-green-50 hover:bg-green-100 text-green-700 text-xs font-semibold transition-colors">
              <i data-lucide="link" class="w-3.5 h-3.5"></i>
              ${t("javascript.controllers_v2.assembly_editor_controller.connect_from_here")}
            </button>
            ${data.parent_node_id ? `
            <button id="detail-detach-btn"
               class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700 text-xs font-semibold transition-colors">
              <i data-lucide="scissors" class="w-3.5 h-3.5"></i>
              ${t("javascript.controllers_v2.assembly_editor_controller.detach")}
            </button>
            ` : ''}
            <button id="detail-remove-btn"
               class="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold transition-colors">
              <i data-lucide="trash-2" class="w-3.5 h-3.5"></i>
              ${t("javascript.controllers_v2.assembly_editor_controller.remove")}
            </button>
          </div>
        </div>
      </div>
    `
    detailPanel.classList.remove('hidden')

    createIcons({ icons: ASSEMBLY_ICONS, root: detailPanel })

    // Wire "Show X more" for Feeds → section
    const feedsShowMore = detailPanel.querySelector('#feeds-show-more')
    if (feedsShowMore && allConnItems.length > 0) {
      feedsShowMore.addEventListener('click', () => {
        const feedsList = detailPanel.querySelector('#feeds-list')
        if (feedsList) {
          feedsList.innerHTML = allConnItems.join('')
          feedsShowMore.remove()
          feedsList.querySelectorAll('[data-highlight-node]').forEach(btn => {
            btn.addEventListener('click', (e) => { e.stopPropagation(); this.highlightNode(btn.dataset.highlightNode) })
          })
        }
      })
    }

    const statusToggle = detailPanel.querySelector('#detail-status-toggle')
    const statusPicker = detailPanel.querySelector('#detail-status-picker')
    statusToggle?.addEventListener('click', (e) => { e.stopPropagation(); statusPicker?.classList.toggle('hidden') })
    detailPanel.querySelectorAll('[data-status-pick]').forEach(btn => {
      btn.addEventListener('click', () => {
        statusPicker?.classList.add('hidden')
        this.setNodeStatus(data.id, btn.dataset.statusPick)
      })
    })

    detailPanel.querySelector('#detail-close-btn')?.addEventListener('click', () => this.hideNodeDetails())
    detailPanel.querySelector('#detail-collapse-btn')?.addEventListener('click', () => this.toggleCollapse(data.id))
    this.attachBackupListeners(detailPanel, String(data.id))

    detailPanel.querySelector('#detail-connect-btn')?.addEventListener('click', (e) => {
      e.stopPropagation()
      this.hideNodeDetails()
      this.enterConnectMode(String(data.id))
    })
    detailPanel.querySelector('#detail-detach-btn')?.addEventListener('click', (e) => {
      e.stopPropagation()
      this.detachNode(String(data.id))
    })
    detailPanel.querySelector('#detail-remove-btn')?.addEventListener('click', (e) => {
      e.stopPropagation()
      this.removeNodeById(String(data.id))
    })

    // Highlight any backup/parent/connection link on click
    detailPanel.querySelectorAll('[data-highlight-node]').forEach(btn => {
      btn.addEventListener('click', (e) => { e.stopPropagation(); this.highlightNode(btn.dataset.highlightNode) })
    })

    // Add sub-component inline search
    const addChildBtn = detailPanel.querySelector('#detail-add-child-btn')
    const addChildWrap = detailPanel.querySelector('#detail-add-child-wrap')
    addChildBtn?.addEventListener('click', (e) => {
      e.stopPropagation()
      addChildWrap?.classList.toggle('hidden')
      if (!addChildWrap?.classList.contains('hidden')) detailPanel.querySelector('#detail-add-child-input')?.focus()
    })
    this.attachDetailAddChildSearch(detailPanel, String(data.id))
  }

  // Build the backup section HTML — supports both directions:
  //   PRIMARY perspective: "Backup Unit" row (assign/remove the asset that backs up this node)
  //   BACKUP perspective:  "Backup For"  row (this node is a standby for another primary)
  buildBackupSection(nodeId, hasBackup, backupForId, backupUnitId, backupUnitName, backupUnitNodeId, backupForNodeId) {
    const rows = []

    // PRIMARY perspective ─ this node has (or can have) a backup unit
    if (hasBackup) {
      const buLabel = this.escapeHtml(backupUnitName || t('javascript.controllers_v2.assembly_editor_controller.assigned'))
      // Use assembly_node ID to find in Cytoscape (not asset ID)
      const buNodeId = backupUnitNodeId || backupUnitId
      const buInGraph = buNodeId && this.cy?.getElementById(String(buNodeId))?.length
      const buDisplay = buInGraph
        ? `<button data-highlight-node="${buNodeId}"
                   class="text-xs font-semibold text-green-600 hover:text-green-700 hover:underline truncate max-w-[100px] transition-colors"
                   title="${t("javascript.controllers_v2.assembly_editor_controller.highlight_in_graph")}">${buLabel}</button>`
        : `<span class="text-xs font-semibold text-green-600">${buLabel}</span>`
      rows.push(`
        <div class="flex justify-between items-center">
          <span class="text-xs text-gray-400">${t("javascript.controllers_v2.assembly_editor_controller.backup_unit")}</span>
          <div class="flex items-center gap-1.5">
            ${buDisplay}
            <button id="backup-remove-unit-btn" class="text-gray-300 hover:text-red-500 transition-colors" title="${t("javascript.controllers_v2.assembly_editor_controller.remove_backup_unit")}">
              <i data-lucide="unlink" class="w-3 h-3"></i>
            </button>
          </div>
        </div>`)
    } else {
      rows.push(`
        <div class="flex justify-between items-center">
          <span class="text-xs text-gray-400">${t("javascript.controllers_v2.assembly_editor_controller.backup_unit")}</span>
          <button id="backup-assign-unit-btn" class="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors">
            <i data-lucide="link-2" class="w-3 h-3"></i> ${t("javascript.controllers_v2.assembly_editor_controller.assign")}
          </button>
        </div>
        <div id="backup-unit-search-wrap" class="hidden mt-1 space-y-1">
          <input id="backup-unit-search-input" type="text" placeholder="${this.escapeHtml(t("javascript.controllers_v2.assembly_editor_controller.search_backup_unit"))}"
                 class="w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary/30">
          <div id="backup-unit-search-results" class="max-h-24 overflow-y-auto rounded-md border border-gray-100 bg-white shadow-sm empty:hidden"></div>
        </div>`)
    }

    // BACKUP perspective ─ this node is a standby for a primary
    if (backupForId) {
      // Use assembly_node ID to find primary in Cytoscape (not asset ID)
      const primaryNodeId = backupForNodeId || backupForId
      const primaryNode = this.cy?.getElementById(String(primaryNodeId))
      const primaryLabel = (primaryNode?.length && primaryNode.data('label')) ? primaryNode.data('label') : t("javascript.controllers_v2.assembly_editor_controller.asset_number", { id: backupForId })
      const primaryInGraph = primaryNode?.length > 0
      rows.push(`
        <div class="flex justify-between items-center">
          <span class="text-xs text-gray-400">${t("javascript.controllers_v2.assembly_editor_controller.backup_for")}</span>
          <div class="flex items-center gap-1.5 min-w-0">
            ${primaryInGraph
              ? `<button data-highlight-node="${primaryNodeId}"
                         class="text-xs font-semibold text-blue-600 hover:text-blue-700 hover:underline truncate max-w-[100px] transition-colors"
                         title="${t("javascript.controllers_v2.assembly_editor_controller.highlight_in_graph")}">${this.escapeHtml(primaryLabel)}</button>`
              : `<span class="text-xs font-semibold text-blue-600 truncate max-w-[100px]" title="${this.escapeHtml(primaryLabel)}">${this.escapeHtml(primaryLabel)}</span>`
            }
            <button id="backup-clear-for-btn" class="flex-shrink-0 text-gray-300 hover:text-red-500 transition-colors" title="${t("javascript.controllers_v2.assembly_editor_controller.clear_backup_assignment")}">
              <i data-lucide="unlink" class="w-3 h-3"></i>
            </button>
          </div>
        </div>`)
    } else {
      rows.push(`
        <div class="flex justify-between items-center">
          <span class="text-xs text-gray-400">${t("javascript.controllers_v2.assembly_editor_controller.backup_for")}</span>
          <button id="backup-assign-for-btn" class="flex items-center gap-1 text-xs text-gray-400 hover:text-primary transition-colors">
            <i data-lucide="link-2" class="w-3 h-3"></i> ${t("javascript.controllers_v2.assembly_editor_controller.set_primary")}
          </button>
        </div>
        <div id="backup-for-search-wrap" class="hidden mt-1 space-y-1">
          <input id="backup-for-search-input" type="text" placeholder="${this.escapeHtml(t("javascript.controllers_v2.assembly_editor_controller.search_primary_asset"))}"
                 class="w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary/30">
          <div id="backup-for-search-results" class="max-h-24 overflow-y-auto rounded-md border border-gray-100 bg-white shadow-sm empty:hidden"></div>
        </div>`)
    }

    return rows.join('')
  }

  attachBackupListeners(panel, nodeId) {
    // PRIMARY side: remove existing backup unit
    panel.querySelector('#backup-remove-unit-btn')?.addEventListener('click', () => {
      this.setBackupUnit(nodeId, null)
    })

    // PRIMARY side: open search to assign a new backup unit
    const assignUnitBtn = panel.querySelector('#backup-assign-unit-btn')
    const unitSearchWrap = panel.querySelector('#backup-unit-search-wrap')
    assignUnitBtn?.addEventListener('click', (e) => {
      e.stopPropagation()
      unitSearchWrap?.classList.toggle('hidden')
      if (!unitSearchWrap?.classList.contains('hidden')) panel.querySelector('#backup-unit-search-input')?.focus()
    })
    this.attachSearchListener(
      panel.querySelector('#backup-unit-search-input'),
      panel.querySelector('#backup-unit-search-results'),
      nodeId,
      (selectedId) => { this.setBackupUnit(nodeId, selectedId); unitSearchWrap?.classList.add('hidden') }
    )

    // BACKUP side: clear this node's backup_for_id
    panel.querySelector('#backup-clear-for-btn')?.addEventListener('click', () => {
      this.setBackupFor(nodeId, null)
    })

    // BACKUP side: open search to assign a primary
    const assignForBtn = panel.querySelector('#backup-assign-for-btn')
    const forSearchWrap = panel.querySelector('#backup-for-search-wrap')
    assignForBtn?.addEventListener('click', (e) => {
      e.stopPropagation()
      forSearchWrap?.classList.toggle('hidden')
      if (!forSearchWrap?.classList.contains('hidden')) panel.querySelector('#backup-for-search-input')?.focus()
    })
    this.attachSearchListener(
      panel.querySelector('#backup-for-search-input'),
      panel.querySelector('#backup-for-search-results'),
      nodeId,
      (selectedId) => { this.setBackupFor(nodeId, selectedId); forSearchWrap?.classList.add('hidden') }
    )
  }

  // Generic search input listener that calls onSelect(id) when a result is clicked
  attachSearchListener(inputEl, resultsEl, excludeId, onSelect) {
    if (!inputEl || !resultsEl) return
    inputEl.addEventListener('input', async (e) => {
      const query = e.target.value.trim()
      if (query.length < 2) { resultsEl.innerHTML = ''; return }
      try {
        const res = await fetch(`${this.searchUrlValue}?query=${encodeURIComponent(query)}&exclude_id=${excludeId}`, {
          headers: { 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken }
        })
        const d = await res.json()
        const assets = (d.assets || []).slice(0, 5)
        if (assets.length === 0) { resultsEl.innerHTML = `<p class="text-xs text-gray-400 px-2 py-1.5">${t("javascript.controllers_v2.assembly_editor_controller.no_results")}</p>`; return }
        resultsEl.innerHTML = assets.map(a =>
          `<button class="w-full text-left px-2 py-1.5 text-xs text-gray-700 hover:bg-primary/5 rounded transition-colors"
                   data-result-id="${this.escapeHtml(a.id)}">${this.escapeHtml(a.name)}</button>`
        ).join('')
        resultsEl.querySelectorAll('[data-result-id]').forEach(btn => {
          btn.addEventListener('click', () => onSelect(btn.dataset.resultId))
        })
      } catch (err) { console.error(err) }
    })
  }

  hideNodeDetails() {
    const detailPanel = document.getElementById('node-detail-panel')
    if (detailPanel) detailPanel.classList.add('hidden')
    this._activeCardEl?.classList.remove('ring-2', 'ring-primary', 'ring-offset-1', 'bg-primary/5')
    this._activeCardEl = null
    this._selectedNode?.unselect()
    this._selectedNode = null
  }

  // Called from sidebar card click — centres the graph on that node and opens detail panel
  focusNode(event) {
    // Ignore clicks on inner interactive elements
    if (event.target.closest('a, button')) return

    const nodeId = String(event.currentTarget.dataset.nodeId)
    if (!nodeId || !this.cy) return

    // If on mobile and graph panel is not actually visible, switch to graph tab first
    // Use getComputedStyle instead of classList.contains('hidden') because the graphPanel
    // element always has the 'hidden' class in HTML; Tailwind's lg:flex overrides it on desktop
    if (this.hasGraphPanelTarget && getComputedStyle(this.graphPanelTarget).display === 'none') {
      this.showGraph()
    }

    // If node is hidden inside a collapsed subtree, expand the collapsed ancestor(s)
    const node = this.cy.getElementById(nodeId)
    if (!node.length) return
    if (node.style('display') === 'none') {
      node.predecessors('node.collapsed').forEach(ancestor => {
        this._collapsedNodes.delete(ancestor.id())
        ancestor.removeClass('collapsed')
        const orig = ancestor.data('_origLabel')
        if (orig) { ancestor.data('label', orig); ancestor.removeData('_origLabel') }
      })
      this._applyCollapsedState()
    }

    // Highlight sidebar card
    this._activeCardEl?.classList.remove('ring-2', 'ring-primary', 'ring-offset-1', 'bg-primary/5')
    this._activeCardEl = event.currentTarget
    this._activeCardEl.classList.add('ring-2', 'ring-primary', 'ring-offset-1', 'bg-primary/5')

    // Animate graph to node
    this.cy.animate({
      center: { eles: node },
      zoom: Math.max(this.cy.zoom(), 1.4),
      duration: 420,
      easing: 'ease-in-out-cubic'
    })

    // Persist selection (unselect previous node first)
    this._selectedNode?.unselect()
    this._selectedNode = node
    node.select()

    // Show detail panel
    this.showNodeDetails(node.data())
  }

  // ═══════════════════════════════════════════════════
  // GRAPH DATA + LAYOUT
  // ═══════════════════════════════════════════════════

  // Hide labels when zoomed out to prevent text overlap on large graphs (10k+ nodes)
  updateLabelsForZoom() {
    const zoom = this.cy.zoom()
    const nodeCount = this.cy.nodes().length

    // Thresholds scale with graph density — larger graphs hide labels sooner
    let hideLeafLabelsAt, hideAllLabelsAt
    if (nodeCount > LARGE_GRAPH_THRESHOLD) {
      hideLeafLabelsAt = 0.7
      hideAllLabelsAt = 0.45
    } else if (nodeCount > MEDIUM_GRAPH_THRESHOLD) {
      hideLeafLabelsAt = 0.5
      hideAllLabelsAt = 0.35
    } else {
      hideLeafLabelsAt = 0.4
      hideAllLabelsAt = 0.25
    }

    // Batch all style updates for performance during rapid zoom events
    this.cy.batch(() => {
      if (zoom < hideAllLabelsAt) {
        // Very zoomed out: hide all labels
        this.cy.nodes().style('label', '')
        this.cy.edges().style('label', '')
      } else if (zoom < hideLeafLabelsAt) {
        // Medium zoom: show only root + assembly labels, hide leaf labels and edge labels
        this.cy.nodes().style('label', '')
        this.cy.nodes('[?is_root]').style('label', 'data(label)')
        this.cy.nodes('[child_count > 0]').style('label', 'data(label)')
        this.cy.edges().style('label', '')
      } else {
        // Zoomed in: show all labels including edge connection info
        this.cy.nodes().style('label', 'data(label)')
        this.cy.edges('[label]').style('label', 'data(label)')
        this.cy.edges('[quantity > 1][!label]').style('label', 'data(quantity)')
      }
    })
  }

  async loadHierarchyData() {
    try {
      const response = await fetch(this.hierarchyUrlValue, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        }
      })
      const data = await response.json()

      if (data.nodes && data.nodes.length > 0) {
        // Cytoscape requires { data: {...} } wrapper — server sends plain objects
        this.cy.add(data.nodes.map(n => ({ data: n })))
        this.cy.add((data.edges || []).map(e => ({ data: e })))

        // Adaptive spacing based on graph size to prevent text collision at scale
        const nodeCount = data.nodes.length
        const nodeSep = nodeCount > LARGE_GRAPH_THRESHOLD ? 120 : nodeCount > MEDIUM_GRAPH_THRESHOLD ? 100 : 80
        const rankSep = nodeCount > LARGE_GRAPH_THRESHOLD ? 140 : nodeCount > MEDIUM_GRAPH_THRESHOLD ? 120 : 100

        // Apply dagre layout for beautiful hierarchical tree
        this._runLayout({
          name: 'dagre',
          rankDir: 'TB',
          nodeSep: nodeSep,
          rankSep: rankSep,
          edgeSep: 30,
          animate: nodeCount <= LARGE_GRAPH_THRESHOLD,
          animationDuration: 800,
          animationEasing: 'ease-in-out-cubic',
          fit: true,
          padding: 50
        })

        // Apply label visibility for initial zoom level
        this.updateLabelsForZoom()
        this.updateStatusBanner(data.nodes.length, data.edges.length)
      } else {
        this.showEmptyState()
      }
    } catch (error) {
      console.error('Failed to load hierarchy data:', error)
      this.showEmptyState()
    }
  }

  updateStatusBanner(nodeCount, edgeCount) {
    if (this.hasStatusBannerTarget) {
      this.statusBannerTarget.textContent = t("javascript.controllers_v2.assembly_editor_controller.graph_summary", { nodes: nodeCount, connections: edgeCount })
    }
  }

  showEmptyState() {
    if (this.hasGraphTarget) {
      this.graphTarget.innerHTML = `
        <div class="flex flex-col items-center justify-center h-full text-center px-6">
          <div class="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center mb-4">
            <i data-lucide="network" class="w-10 h-10 text-white/40"></i>
          </div>
          <h3 class="text-lg font-semibold text-white/70 mb-2">${t("javascript.controllers_v2.assembly_editor_controller.no_sub_components_title")}</h3>
          <p class="text-sm text-white/40 max-w-xs">${t("javascript.controllers_v2.assembly_editor_controller.no_sub_components_body")}</p>
        </div>
      `
      createIcons({ icons: ASSEMBLY_ICONS, root: this.graphTarget })
    }
  }

  // ═══════════════════════════════════════════════════
  // SEARCH + ADD/REMOVE CHILDREN
  // ═══════════════════════════════════════════════════

  // Search for assets to add as children
  async search(event) {
    const query = event.target.value.trim()
    if (query.length < 2) {
      this.clearSearchResults()
      return
    }

    try {
      // Use assembly_id to exclude assets already in this assembly (preferred over exclude_id)
      const params = new URLSearchParams({ query })
      if (this.assemblyIdValue) params.set('exclude_assembly_id', this.assemblyIdValue)
      else if (this.assetIdValue) params.set('exclude_id', this.assetIdValue)

      const response = await fetch(`${this.searchUrlValue}?${params}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        }
      })
      const data = await response.json()
      this.renderSearchResults(data.assets || [])
    } catch (error) {
      console.error('Search failed:', error)
    }
  }

  renderSearchResults(assets) {
    if (!this.hasSearchResultsTarget) return

    if (assets.length === 0) {
      this.searchResultsTarget.innerHTML = `
        <p class="text-sm text-gray-400 px-3 py-2.5">${t("javascript.controllers_v2.assembly_editor_controller.no_matching_assets")}</p>
      `
      return
    }

    const statusDots = {
      active:      'bg-green-500',
      maintenance: 'bg-amber-500',
      missing:     'bg-red-500',
      retired:     'bg-gray-400',
      inactive:    'bg-gray-400',
      emergency:   'bg-red-600 animate-pulse',
      breakdown:   'bg-red-800'
    }

    this.searchResultsTarget.innerHTML = assets.map(asset => {
      const name = this.escapeHtml(asset.name)
      const assetType = this.escapeHtml(asset.asset_type)
      const serialNumber = asset.serial_number
        ? `<span class="text-primary/80 font-semibold">${this.escapeHtml(t("javascript.controllers_v2.assembly_editor_controller.serial_short", { serial: asset.serial_number }))}</span>`
        : ''
      const safeId = this.escapeHtml(asset.id)
      const dot = statusDots[asset.status] || 'bg-gray-400'

      return `
        <button
          class="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-primary/5 transition-colors text-left group"
          data-action="click->assembly-editor#addChild"
          data-child-id="${safeId}"
        >
          <div class="w-2 h-2 rounded-full flex-shrink-0 ${dot}"></div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-gray-800 truncate">${name}</p>
            <p class="text-xs text-gray-400">${assetType}${serialNumber ? ' · ' + serialNumber : ''}</p>
          </div>
          <i data-lucide="circle-plus" class="w-5 h-5 text-gray-300 group-hover:text-primary transition-colors flex-shrink-0"></i>
        </button>
      `
    }).join('')
    createIcons({ icons: ASSEMBLY_ICONS, root: this.searchResultsTarget })
  }

  clearSearchResults() {
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.innerHTML = ''
    }
  }

  async addChild(event) {
    const childId = event.currentTarget.dataset.childId  // asset_id
    if (!childId) return

    try {
      const response = await fetch(this.addChildUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          asset_id: childId,
          quantity: 1,
          position: this.cy ? this.cy.nodes().length : 0
        })
      })

      if (response.ok) {
        const data = await response.json()
        this.cy.elements().remove()
        await this.loadHierarchyData()
        if (data.node) this.addChildToSidebar(data.node)
        if (this.hasSearchInputTarget) this.searchInputTarget.value = ''
        this.clearSearchResults()
        this.flashMessage(t('javascript.controllers_v2.assembly_editor_controller.asset_added'), 'success')
      } else {
        const data = await response.json()
        this.flashMessage(data.error || t('javascript.controllers_v2.assembly_editor_controller.failed_to_add_asset'), 'error')
      }
    } catch (error) {
      console.error('Failed to add child:', error)
      this.flashMessage(t('javascript.controllers_v2.assembly_editor_controller.network_error'), 'error')
    }
  }

  async removeChild(event) {
    const nodeId = event.currentTarget.dataset.nodeId  // assembly_node.id
    if (!nodeId) return
    await this.removeNodeById(nodeId)
  }

  // Mobile tab: show components panel, hide graph
  showComponents() {
    if (this.hasGraphPanelTarget) {
      this.graphPanelTarget.classList.add('hidden')
      this.graphPanelTarget.classList.remove('flex')
    }
    if (this.hasComponentsPanelTarget) {
      this.componentsPanelTarget.classList.remove('hidden')
    }
    const compTab = document.getElementById('assembly-tab-components')
    const graphTab = document.getElementById('assembly-tab-graph')
    if (compTab) compTab.className = 'flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold text-primary border-b-2 border-primary bg-primary/5 transition-all'
    if (graphTab) graphTab.className = 'flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold text-gray-500 border-b-2 border-transparent hover:text-gray-700 transition-all'
  }

  // Mobile tab: show graph panel, hide components
  showGraph() {
    if (this.hasComponentsPanelTarget) {
      this.componentsPanelTarget.classList.add('hidden')
    }
    if (this.hasGraphPanelTarget) {
      this.graphPanelTarget.classList.remove('hidden')
      this.graphPanelTarget.classList.add('flex')
    }
    const compTab = document.getElementById('assembly-tab-components')
    const graphTab = document.getElementById('assembly-tab-graph')
    if (compTab) compTab.className = 'flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold text-gray-500 border-b-2 border-transparent hover:text-gray-700 transition-all'
    if (graphTab) graphTab.className = 'flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold text-primary border-b-2 border-primary bg-primary/5 transition-all'
    // Resize cytoscape after panel becomes visible
    if (this.cy) {
      setTimeout(() => { this.cy.resize(); this.cy.fit(50) }, 50)
    }
  }

  addChildToSidebar(child) {
    if (!this.hasChildListTarget) return

    const statusColors = {
      active:      'bg-green-500',
      maintenance: 'bg-amber-500',
      missing:     'bg-red-500',
      retired:     'bg-gray-400',
      inactive:    'bg-gray-400',
      emergency:   'bg-red-600 animate-pulse',
      breakdown:   'bg-red-800'
    }
    const dotColor = statusColors[child.status] || 'bg-gray-400'
    const name = this.escapeHtml(child.name)
    const assetType = this.escapeHtml(child.asset_type || '')
    const titleized = assetType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    const relationshipBadge = child.relationship_type
      ? ` <span class="inline-flex items-center rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700 border border-violet-200">${this.escapeHtml(this._relationshipLabel(child.relationship_type))}</span>`
      : ''
    const connectionBadge = child.connection_type
      ? ` <span class="inline-flex items-center rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700 border border-sky-200">${this.escapeHtml(this._connectionTypeLabel(child.connection_type))}</span>`
      : ''
    const assetUrl = `${this.companyAssetsUrlValue}/${encodeURIComponent(child.asset_id || child.id)}`
    const quantityBadge = child.assembly_quantity > 1
      ? `· <span class="text-primary font-bold">×${child.assembly_quantity}</span>`
      : ''
    const roleBadge = child.child_count > 0
      ? `· <span class="font-bold text-primary/70">${t("javascript.controllers_v2.assembly_editor_controller.sub_assembly")}</span>`
      : ''
    const serialLine = child.serial_number
      ? `<p class="text-[10px] text-gray-300 mt-0.5 font-mono truncate leading-tight">${this.escapeHtml(t("javascript.controllers_v2.assembly_editor_controller.serial_short", { serial: child.serial_number }))}</p>`
      : (child.location ? `<p class="text-[10px] text-gray-300 mt-0.5 font-mono truncate leading-tight">${this.escapeHtml(child.location)}</p>` : '')

    const cardHtml = `
      <div class="group flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white border border-gray-200 hover:border-primary/30 hover:shadow-sm transition-all cursor-pointer"
           data-child-card="${child.id}"
           data-action="click->assembly-editor#focusNode"
           data-node-id="${child.id}">
        <div class="w-2 h-2 rounded-full flex-shrink-0 ${dotColor}"></div>
        <div class="flex-1 min-w-0">
          <span class="text-sm font-semibold text-gray-800 truncate block">${name}</span>
          <p class="text-xs text-gray-400 mt-0.5 truncate">${titleized} ${relationshipBadge} ${connectionBadge} ${roleBadge} ${quantityBadge}</p>
          ${serialLine}
        </div>
        <a href="${assetUrl}" target="_blank"
           class="opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto p-1 rounded-md hover:bg-blue-50 text-gray-300 hover:text-blue-500 transition-all"
           title="${t("javascript.controllers_v2.assembly_editor_controller.open_asset_page")}">
          <i data-lucide="external-link" class="w-4 h-4"></i>
        </a>
        <button class="opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto p-1 rounded-md hover:bg-gray-100 text-gray-300 hover:text-gray-600 transition-all collapse-toggle-btn"
                data-action="click->assembly-editor#toggleCollapseFromSidebar"
                data-node-id="${child.id}"
                title="${t("javascript.controllers_v2.assembly_editor_controller.hide_from_graph")}">
          <i data-lucide="eye" class="w-4 h-4"></i>
        </button>
        <button class="opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto p-1 rounded-md hover:bg-red-50 text-gray-300 hover:text-red-500 transition-all"
                data-action="click->assembly-editor#removeChild"
                data-node-id="${child.id}"
                title="${t("javascript.controllers_v2.assembly_editor_controller.mark_component_removed")}">
          <i data-lucide="circle-minus" class="w-4 h-4"></i>
        </button>
      </div>
    `

    if (this.hasEmptyStateTarget) this.emptyStateTarget.remove()

    let itemsContainer
    if (this.hasChildItemsTarget) {
      itemsContainer = this.childItemsTarget
    } else {
      itemsContainer = document.createElement('div')
      itemsContainer.className = 'space-y-2'
      itemsContainer.dataset.assemblyEditorTarget = 'childItems'
      const parentInfo = this.childListTarget.querySelector('.mt-6')
      if (parentInfo) {
        this.childListTarget.insertBefore(itemsContainer, parentInfo)
      } else {
        this.childListTarget.appendChild(itemsContainer)
      }
    }

    itemsContainer.insertAdjacentHTML('beforeend', cardHtml)
    // Activate any lucide icons in the new card
    const newCard = itemsContainer.lastElementChild
    if (newCard) createIcons({ icons: ASSEMBLY_ICONS, root: newCard })
    this.updateChildCount(1)
  }

  removeChildFromSidebar(childId) {
    if (!this.hasChildListTarget) return

    const card = this.childListTarget.querySelector(`[data-child-card="${childId}"]`)
    if (card) card.remove()

    this.updateChildCount(-1)

    if (this.hasChildItemsTarget && this.childItemsTarget.children.length === 0) {
      this.childItemsTarget.remove()
      this.showSidebarEmptyState()
    }
  }

  updateChildCount(delta) {
    const targets = [
      this.hasChildCountTarget ? this.childCountTarget : null,
      this.hasChildCountBadgeTarget ? this.childCountBadgeTarget : null
    ].filter(Boolean)
    targets.forEach(el => {
      const current = parseInt(el.textContent) || 0
      el.textContent = Math.max(0, current + delta)
    })
  }

  showSidebarEmptyState() {
    const emptyHtml = `
      <div class="flex flex-col items-center justify-center py-10 text-center" data-assembly-editor-target="emptyState">
        <div class="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
          <i data-lucide="layout-grid" class="w-6 h-6 text-gray-400"></i>
        </div>
        <p class="text-sm font-medium text-gray-500 mb-1">${t("javascript.controllers_v2.assembly_editor_controller.no_sub_components_sidebar")}</p>
        <p class="text-xs text-gray-400">${t("javascript.controllers_v2.assembly_editor_controller.search_above_to_add")}</p>
      </div>
    `
    const parentInfo = this.childListTarget.querySelector('.mt-6')
    if (parentInfo) {
      parentInfo.insertAdjacentHTML('beforebegin', emptyHtml)
    } else {
      this.childListTarget.insertAdjacentHTML('beforeend', emptyHtml)
    }
    const emptyEl = this.childListTarget.querySelector('[data-assembly-editor-target="emptyState"]')
    if (emptyEl) createIcons({ icons: ASSEMBLY_ICONS, root: emptyEl })
  }

  // ═══════════════════════════════════════════════════
  // LAYOUT CONTROLS
  // ═══════════════════════════════════════════════════

  layoutDagre() {
    this._runLayout({
      name: 'dagre',
      rankDir: 'TB',
      nodeSep: 80,
      rankSep: 100,
      animate: true,
      animationDuration: 600,
      fit: true,
      padding: 50
    })
  }

  layoutCircle() {
    this._runLayout({
      name: 'circle',
      animate: true,
      animationDuration: 600,
      fit: true,
      padding: 50
    })
  }

  layoutBreadthfirst() {
    this._runLayout({
      name: 'breadthfirst',
      directed: true,
      animate: true,
      animationDuration: 600,
      fit: true,
      padding: 50,
      spacingFactor: 1.5
    })
  }

  layoutCose() {
    this._runLayout({
      name: 'cose',
      animate: true,
      animationDuration: 600,
      fit: true,
      padding: 50,
      nodeRepulsion: () => 8000,
      idealEdgeLength: () => 120
    })
  }

  // Runs a layout after stripping backup edges, then re-adds them when done
  _runLayout(options) {
    this.cy.edges('.backup-edge').remove()
    const layout = this.cy.layout(options)
    layout.one('layoutstop', () => {
      this._addBackupEdges()
      this.updateLabelsForZoom()
    })
    layout.run()
  }

  // Add visual-only backup edges between nodes that are both present in the graph
  _addBackupEdges() {
    this.cy.edges('.backup-edge').remove()
    this.cy.nodes().forEach(node => {
      const backupForId = node.data('backup_for_id')
      if (!backupForId) return
      const primary = this.cy.getElementById(String(backupForId))
      if (!primary.length) return
      const edgeId = `backup-${backupForId}-${node.id()}`
      if (this.cy.getElementById(edgeId).length) return
      this.cy.add({
        group: 'edges',
        data: { id: edgeId, source: String(backupForId), target: node.id(), edge_type: 'backup' },
        classes: 'backup-edge'
      })
    })
  }

  fitGraph() {
    this.cy.fit(50)
    this.cy.animate({
      fit: { padding: 50 },
      duration: 400,
      easing: 'ease-in-out-cubic'
    })
  }

  zoomIn() {
    this.cy.animate({
      zoom: this.cy.zoom() * 1.3,
      duration: 200
    })
  }

  zoomOut() {
    this.cy.animate({
      zoom: this.cy.zoom() / 1.3,
      duration: 200
    })
  }

  // ═══════════════════════════════════════════════════
  // UTILITIES
  // ═══════════════════════════════════════════════════

  flashMessage(message, type = 'success') {
    const flash = document.createElement('div')
    const bgColor = type === 'success'
      ? 'bg-green-600 border-green-500'
      : 'bg-red-600 border-red-500'

    flash.className = `fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg border ${bgColor} text-white text-sm font-semibold shadow-xl transition-all duration-300 transform translate-y-4 opacity-0`
    flash.textContent = message
    document.body.appendChild(flash)

    requestAnimationFrame(() => {
      flash.classList.remove('translate-y-4', 'opacity-0')
    })

    setTimeout(() => {
      flash.classList.add('translate-y-4', 'opacity-0')
      setTimeout(() => flash.remove(), 300)
    }, 3000)
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }

  // Build alert banner HTML for emergency/breakdown statuses
  buildAlertBanner(status) {
    if (status === 'emergency') {
      return `<div class="px-4 py-2 bg-red-600 text-white text-xs font-bold flex items-center gap-1.5">
        <i data-lucide="triangle-alert" class="w-3.5 h-3.5"></i>${t("javascript.controllers_v2.assembly_editor_controller.emergency_banner")}</div>`
    }
    if (status === 'breakdown') {
      return `<div class="px-4 py-2 bg-red-800 text-white text-xs font-bold flex items-center gap-1.5">
        <i data-lucide="triangle-alert" class="w-3.5 h-3.5"></i>${t("javascript.controllers_v2.assembly_editor_controller.breakdown_banner")}</div>`
    }
    return ''
  }

  // Translated label for an enum value (status / connection type / relationship type),
  // falling back to the humanized identifier when no translation exists.
  _enumLabel(group, value) {
    if (!value) return ''
    const key = `${ASSEMBLY_I18N}.${group}.${value}`
    const label = t(key)
    return label === key ? this.humanizeText(value) : label
  }

  _statusLabel(value) { return this._enumLabel('statuses', value) }
  _connectionTypeLabel(value) { return this._enumLabel('connection_types', value) }
  _relationshipLabel(value) { return this._enumLabel('relationship_types', value) }

  humanizeText(text) {
    return String(text || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).trim()
  }

  // Escape HTML to prevent XSS in dynamic content
  escapeHtml(text) {
    if (text == null) return ''
    const str = String(text)
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }
    return str.replace(/[&<>"']/g, m => map[m])
  }

  // ── Inline add-child search from node detail panel ────────────────────────

  attachDetailAddChildSearch(panel, parentId) {
    const input = panel.querySelector('#detail-add-child-input')
    const results = panel.querySelector('#detail-add-child-results')
    if (!input || !results) return

    input.addEventListener('input', async (e) => {
      const query = e.target.value.trim()
      if (query.length < 2) { results.innerHTML = ''; return }
      try {
        // Search all company assets — no exclude_assembly_id
        // We distinguish "add new" vs "move existing" by checking the cy graph
        const params = new URLSearchParams({ query })
        const res = await fetch(`${this.searchUrlValue}?${params}`, {
          headers: { 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken }
        })
        const d = await res.json()
        const assets = (d.assets || []).slice(0, 8)
        if (assets.length === 0) {
          results.innerHTML = `<p class="text-xs text-gray-400 px-2 py-1.5">${t("javascript.controllers_v2.assembly_editor_controller.no_results")}</p>`
          return
        }
        results.innerHTML = assets.map(a => {
          const name = this.escapeHtml(a.name)
          const type = this.escapeHtml(a.asset_type || '')
          // Check if this asset is already somewhere in the assembly graph
          const existingNodes = this.cy ? this.cy.nodes().filter(n => n.data('asset_id') === String(a.id)) : []
          const isInAssembly = existingNodes.length > 0
          const existingNodeId = isInAssembly ? existingNodes[0].id() : ''
          const actionLabel = isInAssembly ? t('javascript.controllers_v2.assembly_editor_controller.move_here') : t('javascript.controllers_v2.assembly_editor_controller.add_here')
          const actionColor = isInAssembly ? 'text-amber-600' : 'text-primary'
          return `<button class="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-primary/5 text-left text-xs rounded"
                          data-add-to-node-id="${parentId}"
                          data-child-asset-id="${this.escapeHtml(String(a.id))}"
                          data-existing-node-id="${existingNodeId}">
            <span class="truncate font-semibold text-gray-800 flex-1">${name}</span>
            <span class="text-gray-400 flex-shrink-0 text-[10px]">${type}</span>
            <span class="${actionColor} flex-shrink-0 font-semibold text-[10px]">${actionLabel}</span>
          </button>`
        }).join('')
        results.querySelectorAll('[data-add-to-node-id]').forEach(btn => {
          btn.addEventListener('click', () => {
            const existingNodeId = btn.dataset.existingNodeId
            if (existingNodeId) {
              this.reparentNode(existingNodeId, btn.dataset.addToNodeId)
            } else {
              this.addChildToNode(btn.dataset.childAssetId, btn.dataset.addToNodeId)
            }
            input.value = ''
            results.innerHTML = ''
            panel.querySelector('#detail-add-child-wrap')?.classList.add('hidden')
          })
        })
      } catch (err) {
        console.error('Detail add-child search error:', err)
      }
    })
  }

  // ── Connect Mode ──────────────────────────────────────────────────────────
  // Activated by toolbar button, right-click menu, or detail panel button.
  // First click = set source node (green ring); second click = reparent target under source.

  enterConnectMode(sourceNodeId = null) {
    this._connectMode = true
    this._connectSource = null
    const btn = document.getElementById('connect-mode-btn')
    if (btn) btn.classList.add('!text-green-500', '!bg-green-50')
    this._setConnectHint(sourceNodeId ? t('javascript.controllers_v2.assembly_editor_controller.connect_hint_with_source') : t('javascript.controllers_v2.assembly_editor_controller.connect_hint_start'))
    if (sourceNodeId && this.cy) {
      const node = this.cy.getElementById(sourceNodeId)
      if (node?.length) {
        this._connectSource = node
        node.addClass('connect-source')
        this._setConnectHint(t('javascript.controllers_v2.assembly_editor_controller.connect_hint_target'))
      }
    }
  }

  exitConnectMode() {
    this._connectMode = false
    this._connectSource?.removeClass('connect-source')
    this._connectSource = null
    document.getElementById('connect-mode-btn')?.classList.remove('!text-green-500', '!bg-green-50')
    this._setConnectHint(null)
  }

  toggleConnectMode() {
    if (this._connectMode) {
      this.exitConnectMode()
    } else {
      this.enterConnectMode()
    }
  }

  _setConnectHint(msg) {
    let hint = document.getElementById('connect-mode-hint')
    if (!msg) { hint?.remove(); return }
    if (!hint) {
      hint = document.createElement('div')
      hint.id = 'connect-mode-hint'
      hint.className = 'absolute top-3 left-1/2 -translate-x-1/2 z-30 px-4 py-2 rounded-full bg-green-600 text-white text-xs font-semibold shadow-lg pointer-events-none'
      this.graphTarget?.appendChild(hint)
    }
    hint.textContent = msg
  }

  // Reparent an existing assembly node under a new parent
  async reparentNode(nodeId, newParentNodeId) {
    try {
      const response = await fetch(this.updateEdgeUrlValue, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken },
        body: JSON.stringify({ node_id: nodeId, parent_node_id: newParentNodeId })
      })
      if (response.ok) {
        this.cy.elements().remove()
        await this.loadHierarchyData()
        this.flashMessage(t('javascript.controllers_v2.assembly_editor_controller.component_moved'), 'success')
      } else {
        const data = await response.json()
        this.flashMessage(data.error || t('javascript.controllers_v2.assembly_editor_controller.failed_to_move_component'), 'error')
      }
    } catch (error) {
      console.error('reparentNode error:', error)
      this.flashMessage(t('javascript.controllers_v2.assembly_editor_controller.network_error'), 'error')
    }
  }

  // Remove parent_node_id from a node — makes it a root-level node in the assembly
  async detachNode(nodeId) {
    try {
      const response = await fetch(this.updateEdgeUrlValue, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken },
        body: JSON.stringify({ node_id: nodeId, parent_node_id: '' })
      })
      if (response.ok) {
        this.cy.elements().remove()
        await this.loadHierarchyData()
        this.hideNodeDetails()
        this.flashMessage(t('javascript.controllers_v2.assembly_editor_controller.node_detached'), 'success')
      } else {
        const data = await response.json()
        this.flashMessage(data.error || t('javascript.controllers_v2.assembly_editor_controller.failed_to_detach'), 'error')
      }
    } catch (error) {
      console.error('detachNode error:', error)
      this.flashMessage(t('javascript.controllers_v2.assembly_editor_controller.network_error'), 'error')
    }
  }

  // Mark a node removed from the assembly while preserving history
  async removeNodeById(nodeId) {
    if (!confirm(t('javascript.controllers_v2.assembly_editor_controller.confirm_mark_removed'))) return
    const reason = window.prompt(t('javascript.controllers_v2.assembly_editor_controller.removal_reason_prompt'), '')
    if (reason === null) return
    try {
      const response = await fetch(this.removeChildUrlValue, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken },
        body: JSON.stringify({ node_id: nodeId, reason })
      })
      if (response.ok) {
        this.cy.elements().remove()
        await this.loadHierarchyData()
        this.hideNodeDetails()
        this.removeChildFromSidebar(nodeId)
        this.flashMessage(t('javascript.controllers_v2.assembly_editor_controller.component_removed'), 'success')
      } else {
        const data = await response.json()
        this.flashMessage(data.error || t('javascript.controllers_v2.assembly_editor_controller.failed_to_remove'), 'error')
      }
    } catch (error) {
      console.error('removeNodeById error:', error)
      this.flashMessage(t('javascript.controllers_v2.assembly_editor_controller.network_error'), 'error')
    }
  }

  async addChildToNode(childId, parentId) {
    try {
      const response = await fetch(this.addChildUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          asset_id: childId,
          parent_node_id: parentId,
          quantity: 1,
          position: this.cy ? this.cy.nodes().length : 0
        })
      })
      if (response.ok) {
        this.cy.elements().remove()
        await this.loadHierarchyData()
        this.flashMessage(t('javascript.controllers_v2.assembly_editor_controller.component_added'), 'success')
      } else {
        const data = await response.json()
        this.flashMessage(data.error || t('javascript.controllers_v2.assembly_editor_controller.failed_to_add_component'), 'error')
      }
    } catch (error) {
      console.error('Failed to add child to node:', error)
      this.flashMessage(t('javascript.controllers_v2.assembly_editor_controller.network_error'), 'error')
    }
  }

  // Creates a brand-new asset and adds it as a child of the focused node in the assembly
  async quickCreateChild(event) {
    if (!this.hasQuickCreateUrlValue) return
    const parentNodeId = event.currentTarget?.dataset?.parentNodeId || null
    const name = prompt(t('javascript.controllers_v2.assembly_editor_controller.new_asset_name_prompt'))
    if (!name?.trim()) return

    try {
      const response = await fetch(this.quickCreateUrlValue, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken },
        body: JSON.stringify({ name: name.trim(), parent_node_id: parentNodeId })
      })
      const data = await response.json()
      if (response.ok) {
        this.cy.elements().remove()
        await this.loadHierarchyData()
        this.flashMessage(t('javascript.controllers_v2.assembly_editor_controller.named_asset_added', { name: data.node?.name || name }), 'success')
      } else {
        this.flashMessage(data.error || t('javascript.controllers_v2.assembly_editor_controller.failed_to_create_asset'), 'error')
      }
    } catch (err) {
      console.error(err)
      this.flashMessage(t('javascript.controllers_v2.assembly_editor_controller.network_error'), 'error')
    }
  }

  // ── Multi-select bar ───────────────────────────────────────────────────────

  _syncMultiBar() {
    const bar = document.getElementById('multi-select-bar')
    const countEl = document.getElementById('multi-select-count')
    if (!bar) return
    const n = this._multiSelected.size
    if (n === 0) {
      bar.classList.add('hidden')
      return
    }
    bar.classList.remove('hidden')
    countEl && (countEl.textContent = t("javascript.controllers_v2.assembly_editor_controller.n_selected", { count: n }))
  }

  _clearMultiSelected() {
    if (!this._multiSelected) return
    this._multiSelected.forEach((node) => node.removeClass('multi-selected'))
    this._multiSelected.clear()
    const bar = document.getElementById('multi-select-bar')
    bar?.classList.add('hidden')
    document.getElementById('multi-status-picker')?.classList.add('hidden')
    document.getElementById('multi-move-wrap')?.classList.add('hidden')
  }

  async batchSetStatus(status, includeDescendants = false) {
    if (!this._multiSelected.size) return
    const nodeIds = Array.from(this._multiSelected.keys())
    try {
      const resp = await fetch(this.batchUpdateUrlValue, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': this.csrfToken },
        body: JSON.stringify({ node_ids: nodeIds, status, retire_descendants: includeDescendants })
      })
      if (resp.ok) {
        const result = await resp.json()
        this._clearMultiSelected()
        this.cy?.elements().remove()
        await this.loadHierarchyData()
        this.flashMessage(t("javascript.controllers_v2.assembly_editor_controller.batch_status_updated", { count: result.count, status: this._statusLabel(status) }), 'success')
      } else {
        const err = await resp.json()
        this.flashMessage(err.error || t('javascript.controllers_v2.assembly_editor_controller.failed_to_update'), 'error')
      }
    } catch (e) {
      console.error('batchSetStatus error:', e)
      this.flashMessage(t('javascript.controllers_v2.assembly_editor_controller.network_error'), 'error')
    }
  }

  async batchMove(parentId) {
    if (!this._multiSelected.size || !parentId) return
    const nodeIds = Array.from(this._multiSelected.keys())
    try {
      const resp = await fetch(this.batchUpdateUrlValue, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': this.csrfToken },
        body: JSON.stringify({ node_ids: nodeIds, parent_node_id: parentId })
      })
      if (resp.ok) {
        const result = await resp.json()
        this._clearMultiSelected()
        this.cy?.elements().remove()
        await this.loadHierarchyData()
        this.flashMessage(t("javascript.controllers_v2.assembly_editor_controller.batch_moved", { count: result.count }), 'success')
      } else {
        const err = await resp.json()
        this.flashMessage(err.error || t('javascript.controllers_v2.assembly_editor_controller.failed_to_move'), 'error')
      }
    } catch (e) {
      console.error('batchMove error:', e)
      this.flashMessage(t('javascript.controllers_v2.assembly_editor_controller.network_error'), 'error')
    }
  }

  _attachMoveSearch() {
    const input = document.getElementById('multi-move-input')
    const results = document.getElementById('multi-move-results')
    if (!input || !results) return
    let debounceTimer = null
    input.addEventListener('input', () => {
      clearTimeout(debounceTimer)
      const q = input.value.trim()
      if (q.length < 2) { results.innerHTML = ''; return }
      debounceTimer = setTimeout(() => {
        // Search within the current graph nodes — filter Cytoscape nodes by label
        if (!this.cy) return
        const lq = q.toLowerCase()
        const matches = this.cy.nodes().filter(n =>
          (n.data('label') || '').toLowerCase().includes(lq)
        ).slice(0, 8)
        if (matches.length === 0) {
          results.innerHTML = `<p class="px-3 py-2 text-xs text-gray-400">${t("javascript.controllers_v2.assembly_editor_controller.no_nodes_found")}</p>`
          return
        }
        results.innerHTML = matches.map(n => {
          const label = this.escapeHtml(n.data('label') || t("javascript.controllers_v2.assembly_editor_controller.node_number", { id: n.id() }))
          const type  = this.escapeHtml(n.data('asset_type') || '')
          return `<button class="w-full px-3 py-1.5 text-left text-xs font-medium text-gray-700 hover:bg-primary/5 hover:text-primary transition-colors flex items-center justify-between gap-1"
                          data-move-to="${n.id()}">
            <span>${label}</span>
            <span class="text-gray-400 text-[10px]">${type}</span>
          </button>`
        }).join('')
        results.querySelectorAll('[data-move-to]').forEach(btn => {
          btn.addEventListener('click', () => {
            document.getElementById('multi-move-wrap')?.classList.add('hidden')
            input.value = ''
            results.innerHTML = ''
            this.batchMove(btn.dataset.moveTo)  // now a Cytoscape node ID (assembly_node.id)
          })
        })
      }, 280)
    })
  }

  _setupMultiSelectBar() {
    const bar = document.getElementById('multi-select-bar')
    if (!bar) return

    // Status picker toggle
    document.getElementById('multi-status-btn')?.addEventListener('click', (e) => {
      e.stopPropagation()
      document.getElementById('multi-status-picker')?.classList.toggle('hidden')
      document.getElementById('multi-move-wrap')?.classList.add('hidden')
    })

    // Status option buttons
    bar.querySelectorAll('[data-multi-status]').forEach(btn => {
      btn.addEventListener('click', () => {
        document.getElementById('multi-status-picker')?.classList.add('hidden')
        this.batchSetStatus(btn.dataset.multiStatus, false)
      })
    })

    // Retire subtree
    document.getElementById('multi-retire-btn')?.addEventListener('click', () => {
      if (confirm(t("javascript.controllers_v2.assembly_editor_controller.confirm_retire_subtree"))) {
        this.batchSetStatus('retired', true)
      }
    })

    // Move toggle
    document.getElementById('multi-move-btn')?.addEventListener('click', (e) => {
      e.stopPropagation()
      const wrap = document.getElementById('multi-move-wrap')
      wrap?.classList.toggle('hidden')
      document.getElementById('multi-status-picker')?.classList.add('hidden')
      if (!wrap?.classList.contains('hidden')) document.getElementById('multi-move-input')?.focus()
    })

    // Clear selection
    document.getElementById('multi-clear-btn')?.addEventListener('click', () => this._clearMultiSelected())

    this._attachMoveSearch()

    // Close popovers on outside click
    document.addEventListener('click', (e) => {
      if (!e.target.closest('#multi-status-btn') && !e.target.closest('#multi-status-picker')) {
        document.getElementById('multi-status-picker')?.classList.add('hidden')
      }
      if (!e.target.closest('#multi-move-btn') && !e.target.closest('#multi-move-wrap')) {
        document.getElementById('multi-move-wrap')?.classList.add('hidden')
      }
    })
  }

}

